import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from '../lib/toast';
import Sidebar from '../components/layout/Sidebar';
import {
  Wallet,
  DollarSign,
  Plus,
  Minus,
  Lock,
  Unlock,
  Clock,
  TrendingUp,
  TrendingDown,
  Receipt,
  Calculator,
  AlertTriangle,
  CheckCircle,
  ShoppingCart,
  ArrowUpDown,
  FileText,
  Users,
  Calendar,
  RefreshCw,
  Filter,
  X,
  Eye,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  BarChart3,
  PieChart,
  Package,
  Utensils,
  Truck,
  CreditCard,
  Edit,
  Trash2,
  Ban,
  History,
  MessageCircle,
  Send,
  ExternalLink,
  Phone,
  Download
} from 'lucide-react';

const CashRegister = () => {
  const { api, user } = useAuth();
  const { branchId, branchName, isMatriz } = useBranch();
  const [cashSession, setCashSession] = useState(null);
  const [movements, setMovements] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openAmount, setOpenAmount] = useState('');
  const [closeAmount, setCloseAmount] = useState('');
  const [movementAmount, setMovementAmount] = useState('');
  const [movementReason, setMovementReason] = useState('');
  const [movementType, setMovementType] = useState('in');
  const [showMovementForm, setShowMovementForm] = useState(false);
  
  // States for expandable movement sections
  const [expandedSection, setExpandedSection] = useState(null); // 'ventas', 'compras', 'gastos'
  
  // States for pre-close summary
  const [preCloseSummary, setPreCloseSummary] = useState(null);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closeNotes, setCloseNotes] = useState('');
  const [denominations, setDenominations] = useState({
    bills: [
      { value: 1000, label: 'Billete $1,000', count: 0 },
      { value: 500, label: 'Billete $500', count: 0 },
      { value: 200, label: 'Billete $200', count: 0 },
      { value: 100, label: 'Billete $100', count: 0 },
      { value: 50, label: 'Billete $50', count: 0 },
      { value: 20, label: 'Billete $20', count: 0 },
    ],
    coins: [
      { value: 10, label: 'Moneda $10', count: 0 },
      { value: 5, label: 'Moneda $5', count: 0 },
      { value: 2, label: 'Moneda $2', count: 0 },
      { value: 1, label: 'Moneda $1', count: 0 },
      { value: 0.5, label: 'Moneda $0.50', count: 0 },
    ],
  });
  const [loadingPreClose, setLoadingPreClose] = useState(false);
  
  // New states for tabs
  const [activeTab, setActiveTab] = useState('caja'); // caja, ventas, estadisticas, movimientos, arqueos
  const tabsContainerRef = useRef(null);

  // Helper function to change tab - header is sticky so tabs always visible
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const [salesReport, setSalesReport] = useState({ summary: {}, customers: [] });
  const [allMovements, setAllMovements] = useState({ summary: {}, movements: [] });
  const [arqueos, setArqueos] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null); // Selected customer for side panel
  const [customerSearch, setCustomerSearch] = useState(''); // Search filter for customers
  
  // NEW: States for professional sales view (FUDO-style)
  const [salesOrders, setSalesOrders] = useState({ summary: {}, orders: [], by_payment_method: {}, by_order_type: {} });
  const [selectedSaleOrder, setSelectedSaleOrder] = useState(null);
  
  // Helper function to get local date string (YYYY-MM-DD) regardless of timezone
  const getLocalDateString = (date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const todayLocal = getLocalDateString();
  
  const [salesFilters, setSalesFilters] = useState({
    startDate: todayLocal,
    endDate: todayLocal,
    orderType: '',
    paymentMethod: '',
    status: '',
    search: ''
  });
  const [loadingSales, setLoadingSales] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  
  // State for arqueo detail modal
  const [selectedArqueo, setSelectedArqueo] = useState(null);
  const [arqueoDetail, setArqueoDetail] = useState({ movements: [], sales: [] });
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // State for delete movement modal (admin only)
  const [deleteMovementModal, setDeleteMovementModal] = useState(false);
  const [movementToDelete, setMovementToDelete] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deletingMovement, setDeletingMovement] = useState(false);
  
  // State for period statistics
  const [periodStats, setPeriodStats] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('day');
  const [selectedDate, setSelectedDate] = useState(getLocalDateString());
  const [loadingStats, setLoadingStats] = useState(false);

  // States for Edit/Void Orders (Admin only)
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [voidModalOpen, setVoidModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editReason, setEditReason] = useState('');
  const [voidReason, setVoidReason] = useState('');
  const [editForm, setEditForm] = useState({
    customer_name: '',
    customer_phone: '',
    num_guests: 1,
    discount_amount: 0,
    discount_reason: ''
  });
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(false);
  
  // WhatsApp notification state
  const [showWhatsAppNotification, setShowWhatsAppNotification] = useState(false);
  const [voidedOrderData, setVoidedOrderData] = useState(null);
  const [whatsappConfig, setWhatsappConfig] = useState({
    manager_whatsapp: '',
    notify_on_void: true,
    notify_on_edit: false
  });

  // Check if user is admin
  const isAdmin = user?.role === 'admin' || user?.role === 'gerente';

  useEffect(() => {
    fetchData();
    fetchWhatsAppConfig();
  }, [branchId]); // Recargar cuando cambie la sucursal

  useEffect(() => {
    if (activeTab === 'ventas') fetchSalesOrders();
    if (activeTab === 'movimientos') fetchAllMovements();
    if (activeTab === 'arqueos') fetchArqueos();
    if (activeTab === 'estadisticas') fetchPeriodStats();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'estadisticas') fetchPeriodStats();
  }, [selectedPeriod, selectedDate]);

  // Refetch sales when filters change
  useEffect(() => {
    if (activeTab === 'ventas') fetchSalesOrders();
  }, [salesFilters.startDate, salesFilters.endDate, salesFilters.orderType, salesFilters.paymentMethod, salesFilters.status]);

  // Auto-refresh sales every 30 seconds when on Ventas tab
  useEffect(() => {
    let interval;
    if (activeTab === 'ventas') {
      interval = setInterval(() => {
        fetchSalesOrders();
      }, 30000); // 30 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeTab, salesFilters]);

  // Debounced search for sales
  const handleSalesSearch = (value) => {
    setSalesFilters(prev => ({ ...prev, search: value }));
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => {
      fetchSalesOrders(value); // Pass value directly to avoid stale state
    }, 500); // 500ms debounce
    setSearchTimeout(timeout);
  };

  const fetchWhatsAppConfig = async () => {
    try {
      const res = await api.get('/settings/whatsapp');
      setWhatsappConfig(res.data);
    } catch (error) {
      console.error('Error fetching WhatsApp config:', error);
    }
  };

  const fetchData = async () => {
    try {
      const branchQuery = branchId ? `?branch_id=${branchId}&is_matriz=${isMatriz}` : '';
      const [sessionRes, movementsRes, historyRes] = await Promise.all([
        api.get(`/cash-register/current${branchQuery}`),
        api.get('/cash-register/movements'),
        api.get('/cash-register/history')
      ]);
      
      setCashSession(sessionRes.data);
      setMovements(movementsRes.data);
      setHistory(historyRes.data);
    } catch (error) {
      if (error.response?.status !== 404) {
        toast.error('Error al cargar datos de caja');
      }
    } finally {
      setLoading(false);
    }
  };

  // Delete cash movement (admin only)
  const handleDeleteMovement = async () => {
    if (!movementToDelete || !deleteReason.trim()) {
      toast.error('Debes ingresar un motivo para eliminar');
      return;
    }
    
    setDeletingMovement(true);
    try {
      await api.delete(`/cash-movements/${movementToDelete.movement_id}`, {
        data: { reason: deleteReason }
      });
      toast.success('Movimiento eliminado correctamente');
      setDeleteMovementModal(false);
      setMovementToDelete(null);
      setDeleteReason('');
      fetchData();
      fetchAllMovements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar movimiento');
    } finally {
      setDeletingMovement(false);
    }
  };

  const openDeleteModal = (movement) => {
    setMovementToDelete(movement);
    setDeleteReason('');
    setDeleteMovementModal(true);
  };

  const fetchSalesReport = async () => {
    try {
      const res = await api.get('/cash-register/sales-report');
      setSalesReport(res.data);
    } catch (error) {
      console.error('Error fetching sales report:', error);
    }
  };

  // NEW: Fetch sales orders for professional view
  const fetchSalesOrders = async (searchOverride = null) => {
    setLoadingSales(true);
    try {
      const params = new URLSearchParams();
      if (salesFilters.startDate) params.append('start_date', salesFilters.startDate);
      if (salesFilters.endDate) params.append('end_date', salesFilters.endDate);
      if (salesFilters.orderType) params.append('order_type', salesFilters.orderType);
      if (salesFilters.paymentMethod) params.append('payment_method', salesFilters.paymentMethod);
      if (salesFilters.status) params.append('status', salesFilters.status);
      // Use searchOverride if provided, otherwise use state
      const searchValue = searchOverride !== null ? searchOverride : salesFilters.search;
      if (searchValue) params.append('search', searchValue);
      
      const res = await api.get(`/cash-register/sales-orders?${params.toString()}`);
      setSalesOrders(res.data);
    } catch (error) {
      console.error('Error fetching sales orders:', error);
      toast.error('Error al cargar las ventas');
    } finally {
      setLoadingSales(false);
    }
  };

  const fetchAllMovements = async () => {
    try {
      const res = await api.get('/cash-register/all-movements');
      setAllMovements(res.data);
    } catch (error) {
      console.error('Error fetching movements:', error);
    }
  };

  const fetchArqueos = async () => {
    try {
      const res = await api.get('/cash-register/arqueos');
      setArqueos(res.data);
    } catch (error) {
      console.error('Error fetching arqueos:', error);
    }
  };

  const fetchArqueoDetail = async (arqueo) => {
    setSelectedArqueo(arqueo);
    setLoadingDetail(true);
    try {
      const res = await api.get(`/cash-register/arqueo-detail/${arqueo.session_id}`);
      setArqueoDetail(res.data);
    } catch (error) {
      console.error('Error fetching arqueo detail:', error);
      toast.error('Error al cargar detalle del arqueo');
    } finally {
      setLoadingDetail(false);
    }
  };

  const fetchPeriodStats = async () => {
    setLoadingStats(true);
    try {
      const res = await api.get(`/cash-register/sales-by-period?period=${selectedPeriod}&date=${selectedDate}`);
      setPeriodStats(res.data);
    } catch (error) {
      console.error('Error fetching period stats:', error);
      toast.error('Error al cargar estadísticas');
    } finally {
      setLoadingStats(false);
    }
  };

  const navigatePeriod = (direction) => {
    const current = new Date(selectedDate);
    let newDate;
    
    switch (selectedPeriod) {
      case 'day':
        newDate = new Date(current.setDate(current.getDate() + direction));
        break;
      case 'week':
        newDate = new Date(current.setDate(current.getDate() + (7 * direction)));
        break;
      case 'month':
        newDate = new Date(current.setMonth(current.getMonth() + direction));
        break;
      case 'year':
        newDate = new Date(current.setFullYear(current.getFullYear() + direction));
        break;
      default:
        newDate = current;
    }
    
    setSelectedDate(getLocalDateString(newDate));
  };

  const openCashRegister = async () => {
    const amount = parseFloat(openAmount);
    if (isNaN(amount) || amount < 0) {
      return toast.error('Ingresa un monto válido');
    }

    try {
      const response = await api.post('/cash-register/open', { 
        initial_amount: amount,
        branch_id: branchId  // Sucursal donde se abre la caja
      });
      setCashSession(response.data);
      setOpenAmount('');
      toast.success(`Caja abierta en ${branchName}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al abrir caja');
    }
  };

  // Load pre-close summary before closing
  const loadPreCloseSummary = async () => {
    setLoadingPreClose(true);
    try {
      const res = await api.get('/cash-register/pre-close-summary');
      setPreCloseSummary(res.data);
      setShowCloseModal(true);
      // Reset denominations
      setDenominations({
        bills: [
          { value: 1000, label: 'Billete $1,000', count: 0 },
          { value: 500, label: 'Billete $500', count: 0 },
          { value: 200, label: 'Billete $200', count: 0 },
          { value: 100, label: 'Billete $100', count: 0 },
          { value: 50, label: 'Billete $50', count: 0 },
          { value: 20, label: 'Billete $20', count: 0 },
        ],
        coins: [
          { value: 10, label: 'Moneda $10', count: 0 },
          { value: 5, label: 'Moneda $5', count: 0 },
          { value: 2, label: 'Moneda $2', count: 0 },
          { value: 1, label: 'Moneda $1', count: 0 },
          { value: 0.5, label: 'Moneda $0.50', count: 0 },
        ],
      });
      setCloseNotes('');
      setCloseAmount('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cargar resumen');
    } finally {
      setLoadingPreClose(false);
    }
  };

  // Calculate total from denominations
  const calculateDenominationsTotal = () => {
    const billsTotal = denominations.bills.reduce((sum, d) => sum + (d.value * d.count), 0);
    const coinsTotal = denominations.coins.reduce((sum, d) => sum + (d.value * d.count), 0);
    return billsTotal + coinsTotal;
  };

  // Update denomination count
  const updateDenomination = (type, index, newCount) => {
    const count = parseInt(newCount) || 0;
    setDenominations(prev => ({
      ...prev,
      [type]: prev[type].map((d, i) => i === index ? { ...d, count: Math.max(0, count) } : d)
    }));
  };

  const closeCashRegister = async () => {
    const denominationsTotal = calculateDenominationsTotal();
    const amount = denominationsTotal > 0 ? denominationsTotal : parseFloat(closeAmount);
    
    if (isNaN(amount) || amount < 0) {
      return toast.error('Ingresa el monto de cierre o cuenta las denominaciones');
    }

    // Check for pending orders
    if (preCloseSummary?.alerts?.has_pending) {
      const confirm = window.confirm(`Hay ${preCloseSummary.alerts.pending_orders.length} órdenes pendientes. ¿Deseas continuar con el cierre?`);
      if (!confirm) return;
    }

    try {
      const res = await api.post('/cash-register/close', { 
        final_amount: amount,
        denominations: denominationsTotal > 0 ? denominations : null,
        notes: closeNotes || null
      });
      setCashSession(null);
      setCloseAmount('');
      setShowCloseModal(false);
      setPreCloseSummary(null);
      toast.success('Caja cerrada exitosamente');
      
      // Offer to download PDF
      if (res.data?.report?.session_id) {
        const downloadPdf = window.confirm('¿Deseas descargar el reporte de cierre en PDF?');
        if (downloadPdf) {
          downloadCloseReportPdf(res.data.report.session_id);
        }
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cerrar caja');
    }
  };

  // Download close report PDF
  const downloadCloseReportPdf = async (sessionId) => {
    try {
      const res = await api.get(`/cash-register/close-report-pdf/${sessionId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `corte_caja_${sessionId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Error al descargar PDF');
    }
  };

  const addMovement = async () => {
    const amount = parseFloat(movementAmount);
    if (isNaN(amount) || amount <= 0) {
      return toast.error('Ingresa un monto válido');
    }
    if (!movementReason.trim()) {
      return toast.error('Ingresa el motivo');
    }

    try {
      await api.post('/cash-register/movement', {
        amount,
        movement_type: movementType,
        reason: movementReason
      });
      setMovementAmount('');
      setMovementReason('');
      setShowMovementForm(false);
      toast.success(movementType === 'in' ? 'Entrada registrada' : 'Salida registrada');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar movimiento');
    }
  };

  // ==================== ORDER EDIT/VOID FUNCTIONS ====================
  
  const openEditModal = (order) => {
    setSelectedOrder(order);
    setEditForm({
      customer_name: order.customer_name || '',
      customer_phone: order.customer_phone || '',
      num_guests: order.num_guests > 1 ? order.num_guests : '',
      discount_amount: order.discount_amount > 0 ? order.discount_amount : '',
      discount_reason: order.discount_reason || ''
    });
    setEditReason('');
    setEditModalOpen(true);
  };

  const openVoidModal = (order) => {
    setSelectedOrder(order);
    setVoidReason('');
    setVoidModalOpen(true);
  };

  const handleEditOrder = async () => {
    if (!editReason.trim()) {
      return toast.error('Debe proporcionar un motivo para la edición');
    }

    try {
      // Prepare data with correct types
      const editData = {
        customer_name: editForm.customer_name || null,
        customer_phone: editForm.customer_phone || null,
        num_guests: editForm.num_guests ? parseInt(editForm.num_guests) : null,
        discount_amount: editForm.discount_amount ? parseFloat(editForm.discount_amount) : null,
        discount_reason: editForm.discount_reason || null,
        reason: editReason.trim()
      };
      
      await api.put(`/orders/${selectedOrder.order_id}/edit`, editData);
      toast.success('Orden editada exitosamente');
      setEditModalOpen(false);
      setSelectedOrder(null);
      fetchSalesReport();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al editar la orden');
    }
  };

  const handleVoidOrder = async () => {
    if (!voidReason.trim()) {
      return toast.error('Debe proporcionar un motivo para la anulación');
    }

    try {
      await api.put(`/orders/${selectedOrder.order_id}/void`, {
        reason: voidReason.trim()
      });
      toast.success(`Orden #${selectedOrder.order_number} anulada exitosamente`);
      
      // Show WhatsApp notification only if configured and enabled
      if (whatsappConfig.manager_whatsapp && whatsappConfig.notify_on_void) {
        setVoidedOrderData({
          order_number: selectedOrder.order_number,
          total: selectedOrder.total,
          customer_name: selectedOrder.customer_name || 'General',
          items: selectedOrder.items || [],
          reason: voidReason.trim(),
          voided_by: user?.name || 'Admin',
          voided_at: new Date().toLocaleString('es-MX')
        });
        setShowWhatsAppNotification(true);
      }
      
      setVoidModalOpen(false);
      setSelectedOrder(null);
      fetchSalesReport();
      fetchData(); // Refresh cash register data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al anular la orden');
    }
  };

  // Generate WhatsApp message for voided order
  const generateWhatsAppMessage = (data) => {
    const products = data.items?.map(item => 
      `  • ${item.quantity}x ${item.product_name} - $${item.subtotal?.toFixed(2)}`
    ).join('\n') || '  Sin productos';
    
    const message = `🚨 *VENTA ANULADA*

📋 *Venta #${data.order_number}*
💰 *Monto:* $${data.total?.toFixed(2)}
👤 *Cliente:* ${data.customer_name}

📦 *Productos:*
${products}

❌ *Motivo:* ${data.reason}
👨‍💼 *Anulada por:* ${data.voided_by}
📅 *Fecha:* ${data.voided_at}

_Notificación automática de SmartPOS_`;
    
    return encodeURIComponent(message);
  };

  const openWhatsAppNotification = () => {
    if (voidedOrderData && whatsappConfig.manager_whatsapp) {
      const message = generateWhatsAppMessage(voidedOrderData);
      const phone = whatsappConfig.manager_whatsapp.replace('+', '');
      const whatsappUrl = `https://wa.me/${phone}?text=${message}`;
      window.open(whatsappUrl, '_blank');
    }
  };

  const fetchAuditLog = async (orderId) => {
    setLoadingAudit(true);
    try {
      const res = await api.get(`/orders/${orderId}/audit-log`);
      setAuditLogs(res.data);
      setAuditLogOpen(true);
    } catch (error) {
      toast.error('Error al cargar historial de cambios');
    } finally {
      setLoadingAudit(false);
    }
  };

  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('es-MX', { 
      timeZone: 'America/Hermosillo',
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Use cash_sales (only cash payments) instead of total_sales (all payments)
  // This gives the correct expected amount in the cash register
  const expectedTotal = cashSession ? 
    cashSession.initial_amount + 
    (cashSession.cash_sales || 0) + 
    cashSession.total_cash_in - 
    cashSession.total_cash_out : 0;

  return (
    <div className="min-h-screen bg-background flex">
      <Sidebar />
      
      <main className="flex-1 md:ml-64 p-3 md:p-8 pt-16 md:pt-8">
        <div className="max-w-6xl mx-auto" style={{ minHeight: '100vh' }}>
          {/* Header - Sticky */}
          <div className="mb-4 md:mb-6 sticky top-0 z-40 bg-background pb-2">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="p-2 rounded-lg bg-accent/10">
                  <Wallet className="w-5 h-5 md:w-6 md:h-6 text-accent" />
                </div>
                <div>
                  <h1 className="text-xl md:text-3xl font-chivo font-extrabold" data-testid="cash-register-title">
                    Control de Caja
                  </h1>
                  <p className="text-muted-foreground text-xs md:text-sm hidden sm:block">
                    Entradas y salidas de efectivo
                  </p>
                </div>
              </div>
              <Button variant="outline" size="icon" onClick={fetchData} className="h-9 w-9">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            
            {/* Tabs - Scrollable on mobile */}
            <div ref={tabsContainerRef} className="flex items-center gap-1 bg-muted rounded-lg p-1 overflow-x-auto scrollbar-hide">
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); handleTabChange('caja'); }}
                className={`flex items-center gap-1.5 px-3 md:px-4 py-2 rounded-md text-xs md:text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === 'caja' ? 'bg-white shadow text-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Wallet className="w-3.5 h-3.5 md:w-4 md:h-4" />
                Caja
              </button>
              <button
                type="button"
                data-testid="tab-ventas"
                onClick={(e) => { e.preventDefault(); handleTabChange('ventas'); }}
                className={`flex items-center gap-1.5 px-3 md:px-4 py-2 rounded-md text-xs md:text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === 'ventas' ? 'bg-white shadow text-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <ShoppingCart className="w-3.5 h-3.5 md:w-4 md:h-4" />
                Ventas
              </button>
              <button
                type="button"
                data-testid="tab-estadisticas"
                onClick={(e) => { e.preventDefault(); handleTabChange('estadisticas'); }}
                className={`flex items-center gap-1.5 px-3 md:px-4 py-2 rounded-md text-xs md:text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === 'estadisticas' ? 'bg-white shadow text-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <BarChart3 className="w-3.5 h-3.5 md:w-4 md:h-4" />
                Estadísticas
              </button>
              <button
                type="button"
                data-testid="tab-movimientos"
                onClick={(e) => { e.preventDefault(); handleTabChange('movimientos'); }}
                className={`flex items-center gap-1.5 px-3 md:px-4 py-2 rounded-md text-xs md:text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === 'movimientos' ? 'bg-white shadow text-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <ArrowUpDown className="w-3.5 h-3.5 md:w-4 md:h-4" />
                Movimientos
              </button>
              <button
                type="button"
                data-testid="tab-arqueos"
                onClick={(e) => { e.preventDefault(); handleTabChange('arqueos'); }}
                className={`flex items-center gap-1.5 px-3 md:px-4 py-2 rounded-md text-xs md:text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === 'arqueos' ? 'bg-white shadow text-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <FileText className="w-3.5 h-3.5 md:w-4 md:h-4" />
                Arqueos
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin w-8 h-8 border-4 border-accent border-t-transparent rounded-full" />
            </div>
          ) : activeTab === 'caja' ? (
            /* Caja Tab - Original Content */
            <>
            {!cashSession ? (
            /* Open Cash Register Form */
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="max-w-md mx-auto border border-border/50">
                <CardHeader className="text-center">
                  <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                    <Lock className="w-8 h-8 text-accent" />
                  </div>
                  <CardTitle className="font-chivo">Caja Cerrada</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Ingresa el monto inicial para abrir la caja
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Monto Inicial</Label>
                    <div className="relative mt-1">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={openAmount}
                        onChange={(e) => setOpenAmount(e.target.value)}
                        className="pl-9 text-lg"
                        data-testid="open-amount-input"
                      />
                    </div>
                  </div>
                  <Button 
                    className="w-full bg-accent text-white" 
                    onClick={openCashRegister}
                    data-testid="open-cash-btn"
                  >
                    <Unlock className="w-4 h-4 mr-2" />
                    Abrir Caja
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          ) : (
            /* Active Cash Session */
            <div className="space-y-6">
              {/* Session Status */}
              <Card className="border-green-500 bg-green-50/50">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                        <CheckCircle className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-lg">Caja Abierta</p>
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Abierta: {formatDateTime(cashSession.opened_at)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Abierta por</p>
                      <p className="font-medium">{cashSession.opened_by_name || user?.name}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Summary Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="border border-border/50">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-blue-100">
                        <Wallet className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Inicial</p>
                        <p className="text-xl font-bold">${cashSession.initial_amount.toFixed(2)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border border-border/50">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-green-100">
                        <TrendingUp className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Ventas</p>
                        <p className="text-xl font-bold text-green-600">${(cashSession.total_sales || 0).toFixed(2)}</p>
                        <div className="text-xs text-gray-500 mt-1">
                          <span>💵 ${(cashSession.cash_sales || 0).toFixed(2)}</span>
                          {(cashSession.card_sales > 0 || cashSession.transfer_sales > 0) && (
                            <span className="ml-2">💳 ${((cashSession.card_sales || 0) + (cashSession.transfer_sales || 0)).toFixed(2)}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border border-border/50">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-emerald-100">
                        <Plus className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Entradas</p>
                        <p className="text-xl font-bold text-emerald-600">${cashSession.total_cash_in.toFixed(2)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border border-border/50">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-red-100">
                        <Minus className="w-5 h-5 text-red-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-muted-foreground">Salidas</p>
                        <p className="text-xl font-bold text-red-600">${cashSession.total_cash_out.toFixed(2)}</p>
                        {cashSession.cash_out_breakdown && (
                          <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                            {cashSession.cash_out_breakdown.compras > 0 && (
                              <p>• Compras: <span className="text-red-500">${cashSession.cash_out_breakdown.compras.toFixed(2)}</span></p>
                            )}
                            {cashSession.cash_out_breakdown.gastos_operativos > 0 && (
                              <p>• Gastos Op: <span className="text-orange-500">${cashSession.cash_out_breakdown.gastos_operativos.toFixed(2)}</span></p>
                            )}
                            {cashSession.cash_out_breakdown.otros > 0 && (
                              <p>• Otros: <span className="text-slate-500">${cashSession.cash_out_breakdown.otros.toFixed(2)}</span></p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Totales por Método de Pago - 3 Tarjetas */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Total Esperado en Caja (Efectivo) */}
                <Card className="border-green-500 bg-green-50">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3">
                      <div className="p-3 rounded-xl bg-green-500">
                        <DollarSign className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-green-700 font-medium">Total Esperado en Caja</p>
                        <p className="text-xs text-green-600 mb-1">(Efectivo)</p>
                        <p className="text-2xl font-bold text-green-700">${expectedTotal.toFixed(2)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Total Voucher/Tarjeta */}
                <Card className="border-blue-500 bg-blue-50">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3">
                      <div className="p-3 rounded-xl bg-blue-500">
                        <CreditCard className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-blue-700 font-medium">Total Voucher</p>
                        <p className="text-xs text-blue-600 mb-1">(Tarjeta)</p>
                        <p className="text-2xl font-bold text-blue-700">
                          ${(cashSession?.card_sales || 0).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Total Transferencia */}
                <Card className="border-purple-500 bg-purple-50">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3">
                      <div className="p-3 rounded-xl bg-purple-500">
                        <ArrowUpDown className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-purple-700 font-medium">Total Transferencia</p>
                        <p className="text-xs text-purple-600 mb-1">(Bancaria)</p>
                        <p className="text-2xl font-bold text-purple-700">
                          ${(cashSession?.transfer_sales || 0).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 flex-wrap">
                <Button
                  variant="outline"
                  onClick={() => { setMovementType('in'); setShowMovementForm(true); }}
                  className="flex-1"
                  data-testid="cash-in-btn"
                >
                  <Plus className="w-4 h-4 mr-2 text-green-500" />
                  Entrada de Efectivo
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setMovementType('out'); setShowMovementForm(true); }}
                  className="flex-1"
                  data-testid="cash-out-btn"
                >
                  <Minus className="w-4 h-4 mr-2 text-red-500" />
                  Salida de Efectivo
                </Button>
              </div>

              {/* Movement Form */}
              {showMovementForm && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Card className="border border-border/50">
                    <CardHeader>
                      <CardTitle className="font-chivo flex items-center gap-2">
                        {movementType === 'in' ? (
                          <>
                            <Plus className="w-5 h-5 text-green-500" />
                            Registrar Entrada
                          </>
                        ) : (
                          <>
                            <Minus className="w-5 h-5 text-red-500" />
                            Registrar Salida
                          </>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Monto</Label>
                          <div className="relative mt-1">
                            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="0.00"
                              value={movementAmount}
                              onChange={(e) => setMovementAmount(e.target.value)}
                              className="pl-9"
                              data-testid="movement-amount-input"
                            />
                          </div>
                        </div>
                        <div>
                          <Label>Motivo</Label>
                          <Input
                            placeholder="Ej: Cambio, Pago proveedor..."
                            value={movementReason}
                            onChange={(e) => setMovementReason(e.target.value)}
                            className="mt-1"
                            data-testid="movement-reason-input"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={() => setShowMovementForm(false)}
                          className="flex-1"
                        >
                          Cancelar
                        </Button>
                        <Button
                          className={`flex-1 ${movementType === 'in' ? 'bg-green-500' : 'bg-red-500'}`}
                          onClick={addMovement}
                          data-testid="save-movement-btn"
                        >
                          Guardar
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {/* Movements Table - Always show */}
              {(
                <Card className="border border-border/50">
                  <CardHeader>
                    <CardTitle className="font-chivo flex items-center gap-2">
                      <Receipt className="w-5 h-5" />
                      Movimientos del Día
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {/* Categorized Movement Cards */}
                    {(() => {
                      // Get all sales from session (all payment methods)
                      const totalCashSales = cashSession?.cash_sales || 0;
                      const totalCardSales = cashSession?.card_sales || 0;
                      const totalTransferSales = cashSession?.transfer_sales || 0;
                      const totalAllSales = cashSession?.total_sales || 0;
                      
                      // Categorize movements for display
                      const ventas = movements.filter(m => m.movement_type === 'sale');
                      const compras = movements.filter(m => m.movement_type === 'cash_out' && m.reference_type === 'purchase');
                      const gastos = movements.filter(m => (m.movement_type === 'cash_out' || m.movement_type === 'out') && (m.reference_type === 'expense' || m.reference_type === 'egreso'));
                      const otrosEgresos = movements.filter(m => (m.movement_type === 'out' || m.movement_type === 'cash_out') && !m.reference_type);
                      const entradas = movements.filter(m => m.movement_type === 'in' && m.reference_type);
                      
                      // Use session data for totals (more accurate)
                      const totalVentas = totalAllSales;
                      const totalCompras = compras.reduce((sum, m) => sum + (m.amount || 0), 0);
                      const totalGastos = gastos.reduce((sum, m) => sum + (m.amount || 0), 0);
                      const totalOtros = otrosEgresos.reduce((sum, m) => sum + (m.amount || 0), 0);
                      const totalEntradas = entradas.reduce((sum, m) => sum + (m.amount || 0), 0);
                      
                      const toggleSection = (section, e) => {
                        e.preventDefault();
                        setExpandedSection(expandedSection === section ? null : section);
                      };
                      
                      return (
                        <div className="space-y-3">
                          {/* VENTAS Card - ALL PAYMENT METHODS */}
                          <div className="border border-green-200 rounded-lg overflow-hidden">
                            <button
                              type="button"
                              onClick={(e) => toggleSection('ventas', e)}
                              className="w-full flex items-center justify-between p-4 bg-green-50 hover:bg-green-100 transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-green-500 rounded-lg">
                                  <DollarSign className="w-5 h-5 text-white" />
                                </div>
                                <div className="text-left">
                                  <p className="font-semibold text-green-800">VENTAS DEL DÍA</p>
                                  <p className="text-sm text-green-600">Todos los métodos de pago</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="text-xl font-bold text-green-600">+${totalVentas.toFixed(2)}</span>
                                {expandedSection === 'ventas' ? (
                                  <ChevronDown className="w-5 h-5 text-green-600" />
                                ) : (
                                  <ChevronRight className="w-5 h-5 text-green-600" />
                                )}
                              </div>
                            </button>
                            {expandedSection === 'ventas' && (
                              <div className="border-t border-green-200 bg-white p-4">
                                {/* Payment Method Breakdown */}
                                <div className="grid grid-cols-3 gap-3 mb-4">
                                  <div className="bg-green-50 rounded-lg p-3 text-center">
                                    <div className="flex items-center justify-center gap-2 mb-1">
                                      <DollarSign className="w-4 h-4 text-green-600" />
                                      <span className="text-sm font-medium text-green-700">Efectivo</span>
                                    </div>
                                    <p className="text-lg font-bold text-green-600">${totalCashSales.toFixed(2)}</p>
                                  </div>
                                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                                    <div className="flex items-center justify-center gap-2 mb-1">
                                      <CreditCard className="w-4 h-4 text-blue-600" />
                                      <span className="text-sm font-medium text-blue-700">Tarjeta</span>
                                    </div>
                                    <p className="text-lg font-bold text-blue-600">${totalCardSales.toFixed(2)}</p>
                                  </div>
                                  <div className="bg-purple-50 rounded-lg p-3 text-center">
                                    <div className="flex items-center justify-center gap-2 mb-1">
                                      <ArrowUpDown className="w-4 h-4 text-purple-600" />
                                      <span className="text-sm font-medium text-purple-700">Transferencia</span>
                                    </div>
                                    <p className="text-lg font-bold text-purple-600">${totalTransferSales.toFixed(2)}</p>
                                  </div>
                                </div>
                                
                                {/* Movement details (cash only since card/transfer don't create movements) */}
                                {ventas.length > 0 && (
                                  <div>
                                    <p className="text-xs text-gray-500 mb-2">Detalle de movimientos en efectivo:</p>
                                    <table className="w-full">
                                      <thead className="bg-green-50/50">
                                        <tr>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-green-700">Hora</th>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-green-700">Detalle</th>
                                          <th className="px-4 py-2 text-right text-xs font-medium text-green-700">Monto</th>
                                          {(user?.role === 'admin' || user?.role === 'administrador') && (
                                            <th className="px-4 py-2 w-12"></th>
                                          )}
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-green-100">
                                        {ventas.map((mov, idx) => (
                                          <tr key={idx} className="hover:bg-green-50/30">
                                            <td className="px-4 py-2 text-sm text-gray-500">
                                              {new Date(mov.created_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                                            </td>
                                            <td className="px-4 py-2 text-sm">{mov.reason || 'Venta en efectivo'}</td>
                                            <td className="px-4 py-2 text-sm text-right font-semibold text-green-600">+${mov.amount.toFixed(2)}</td>
                                            {(user?.role === 'admin' || user?.role === 'administrador') && (
                                              <td className="px-4 py-2">
                                                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50" onClick={() => openDeleteModal(mov)}>
                                                  <Trash2 className="w-4 h-4" />
                                                </Button>
                                              </td>
                                            )}
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                                {ventas.length === 0 && totalVentas === 0 && (
                                  <p className="text-center text-gray-500 text-sm">No hay ventas hoy</p>
                                )}
                              </div>
                            )}
                          </div>

                          {/* COMPRAS Card */}
                          <div className="border border-orange-200 rounded-lg overflow-hidden">
                            <button
                              type="button"
                              onClick={(e) => toggleSection('compras', e)}
                              className="w-full flex items-center justify-between p-4 bg-orange-50 hover:bg-orange-100 transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-orange-500 rounded-lg">
                                  <ShoppingCart className="w-5 h-5 text-white" />
                                </div>
                                <div className="text-left">
                                  <p className="font-semibold text-orange-800">COMPRAS</p>
                                  <p className="text-sm text-orange-600">{compras.length} compra(s)</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="text-xl font-bold text-orange-600">-${totalCompras.toFixed(2)}</span>
                                {expandedSection === 'compras' ? (
                                  <ChevronDown className="w-5 h-5 text-orange-600" />
                                ) : (
                                  <ChevronRight className="w-5 h-5 text-orange-600" />
                                )}
                              </div>
                            </button>
                            {expandedSection === 'compras' && compras.length > 0 && (
                              <div className="border-t border-orange-200 bg-white">
                                <div className="divide-y divide-orange-100">
                                  {compras.map((mov, idx) => (
                                    <div key={idx} className="p-4 hover:bg-orange-50/30">
                                      <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2">
                                            <span className="text-sm text-gray-500">
                                              {new Date(mov.created_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                            <span className="font-medium text-gray-800">{mov.reason || 'Compra'}</span>
                                          </div>
                                          {/* Show purchase items if available */}
                                          {mov.purchase_details?.items && mov.purchase_details.items.length > 0 && (
                                            <div className="mt-2 pl-4 border-l-2 border-orange-200">
                                              <p className="text-xs text-orange-600 font-medium mb-1">Detalle de compra:</p>
                                              <ul className="text-xs text-gray-600 space-y-0.5">
                                                {mov.purchase_details.items.slice(0, 5).map((item, i) => (
                                                  <li key={i}>• {item.supply_name || 'Producto'}: {item.quantity} {item.unit || 'pz'} × ${item.unit_cost?.toFixed(2) || '0.00'}</li>
                                                ))}
                                                {mov.purchase_details.items.length > 5 && (
                                                  <li className="text-orange-500">... y {mov.purchase_details.items.length - 5} más</li>
                                                )}
                                              </ul>
                                            </div>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <span className="text-lg font-bold text-orange-600">-${mov.amount.toFixed(2)}</span>
                                          {(user?.role === 'admin' || user?.role === 'administrador') && (
                                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50" onClick={() => openDeleteModal(mov)}>
                                              <Trash2 className="w-4 h-4" />
                                            </Button>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {expandedSection === 'compras' && compras.length === 0 && (
                              <div className="p-4 text-center text-gray-500 text-sm bg-white border-t border-orange-200">
                                No hay compras registradas hoy
                              </div>
                            )}
                          </div>

                          {/* GASTOS OPERATIVOS Card */}
                          <div className="border border-purple-200 rounded-lg overflow-hidden">
                            <button
                              type="button"
                              onClick={(e) => toggleSection('gastos', e)}
                              className="w-full flex items-center justify-between p-4 bg-purple-50 hover:bg-purple-100 transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-purple-500 rounded-lg">
                                  <FileText className="w-5 h-5 text-white" />
                                </div>
                                <div className="text-left">
                                  <p className="font-semibold text-purple-800">GASTOS OPERATIVOS</p>
                                  <p className="text-sm text-purple-600">{gastos.length} gasto(s)</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="text-xl font-bold text-purple-600">-${totalGastos.toFixed(2)}</span>
                                {expandedSection === 'gastos' ? (
                                  <ChevronDown className="w-5 h-5 text-purple-600" />
                                ) : (
                                  <ChevronRight className="w-5 h-5 text-purple-600" />
                                )}
                              </div>
                            </button>
                            {expandedSection === 'gastos' && gastos.length > 0 && (
                              <div className="border-t border-purple-200 bg-white">
                                <table className="w-full">
                                  <thead className="bg-purple-50/50">
                                    <tr>
                                      <th className="px-4 py-2 text-left text-xs font-medium text-purple-700">Hora</th>
                                      <th className="px-4 py-2 text-left text-xs font-medium text-purple-700">Concepto</th>
                                      <th className="px-4 py-2 text-right text-xs font-medium text-purple-700">Monto</th>
                                      {(user?.role === 'admin' || user?.role === 'administrador') && (
                                        <th className="px-4 py-2 w-12"></th>
                                      )}
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-purple-100">
                                    {gastos.map((mov, idx) => (
                                      <tr key={idx} className="hover:bg-purple-50/30">
                                        <td className="px-4 py-2 text-sm text-gray-500">
                                          {new Date(mov.created_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                                        </td>
                                        <td className="px-4 py-2 text-sm">
                                          <span className="font-medium">{mov.expense_details?.category || mov.category || 'Gasto'}</span>
                                          {mov.reason && <span className="text-gray-500 ml-1">- {mov.reason}</span>}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-right font-semibold text-purple-600">-${mov.amount.toFixed(2)}</td>
                                        {(user?.role === 'admin' || user?.role === 'administrador') && (
                                          <td className="px-4 py-2">
                                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50" onClick={() => openDeleteModal(mov)}>
                                              <Trash2 className="w-4 h-4" />
                                            </Button>
                                          </td>
                                        )}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                            {expandedSection === 'gastos' && gastos.length === 0 && (
                              <div className="p-4 text-center text-gray-500 text-sm bg-white border-t border-purple-200">
                                No hay gastos operativos registrados hoy
                              </div>
                            )}
                          </div>

                          {/* OTROS MOVIMIENTOS Card (Entradas/Salidas manuales) */}
                          {(otrosEgresos.length > 0 || entradas.length > 0) && (
                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                              <button
                                type="button"
                                onClick={(e) => toggleSection('otros', e)}
                                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="p-2 bg-gray-500 rounded-lg">
                                    <ArrowUpDown className="w-5 h-5 text-white" />
                                  </div>
                                  <div className="text-left">
                                    <p className="font-semibold text-gray-800">OTROS MOVIMIENTOS</p>
                                    <p className="text-sm text-gray-600">{entradas.length + otrosEgresos.length} movimiento(s)</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-xl font-bold text-gray-600">
                                    {totalEntradas - totalOtros >= 0 ? '+' : ''}${(totalEntradas - totalOtros).toFixed(2)}
                                  </span>
                                  {expandedSection === 'otros' ? (
                                    <ChevronDown className="w-5 h-5 text-gray-600" />
                                  ) : (
                                    <ChevronRight className="w-5 h-5 text-gray-600" />
                                  )}
                                </div>
                              </button>
                              {expandedSection === 'otros' && (
                                <div className="border-t border-gray-200 bg-white">
                                  <table className="w-full">
                                    <thead className="bg-gray-50/50">
                                      <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Hora</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Tipo</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Motivo</th>
                                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-700">Monto</th>
                                        {(user?.role === 'admin' || user?.role === 'administrador') && (
                                          <th className="px-4 py-2 w-12"></th>
                                        )}
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                      {[...entradas, ...otrosEgresos].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((mov, idx) => (
                                        <tr key={idx} className="hover:bg-gray-50/30">
                                          <td className="px-4 py-2 text-sm text-gray-500">
                                            {new Date(mov.created_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                                          </td>
                                          <td className="px-4 py-2 text-sm">
                                            {mov.movement_type === 'in' ? (
                                              <span className="text-green-600 font-medium">Entrada</span>
                                            ) : (
                                              <span className="text-red-600 font-medium">Salida</span>
                                            )}
                                          </td>
                                          <td className="px-4 py-2 text-sm">{mov.reason || '-'}</td>
                                          <td className={`px-4 py-2 text-sm text-right font-semibold ${mov.movement_type === 'in' ? 'text-green-600' : 'text-red-600'}`}>
                                            {mov.movement_type === 'in' ? '+' : '-'}${mov.amount.toFixed(2)}
                                          </td>
                                          {(user?.role === 'admin' || user?.role === 'administrador') && (
                                            <td className="px-4 py-2">
                                              <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50" onClick={() => openDeleteModal(mov)}>
                                                <Trash2 className="w-4 h-4" />
                                              </Button>
                                            </td>
                                          )}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Empty state */}
                          {movements.length === 0 && (
                            <div className="text-center py-8 text-gray-500">
                              <Receipt className="w-12 h-12 mx-auto mb-2 opacity-30" />
                              <p>No hay movimientos registrados hoy</p>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>
              )}

              {/* Close Cash Register */}
              <Card className="border-destructive/50 bg-destructive/5">
                <CardHeader>
                  <CardTitle className="font-chivo flex items-center gap-2 text-destructive">
                    <Lock className="w-5 h-5" />
                    Cerrar Caja
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Totals by Payment Method */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center gap-2 mb-1">
                        <DollarSign className="w-4 h-4 text-green-600" />
                        <span className="text-sm text-green-700 font-medium">Total Esperado Efectivo</span>
                      </div>
                      <p className="text-xl font-bold text-green-800">${expectedTotal.toFixed(2)}</p>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center gap-2 mb-1">
                        <CreditCard className="w-4 h-4 text-blue-600" />
                        <span className="text-sm text-blue-700 font-medium">Total Voucher</span>
                      </div>
                      <p className="text-xl font-bold text-blue-800">${(cashSession?.card_sales || 0).toFixed(2)}</p>
                    </div>
                    <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                      <div className="flex items-center gap-2 mb-1">
                        <ArrowUpDown className="w-4 h-4 text-purple-600" />
                        <span className="text-sm text-purple-700 font-medium">Total Transferencia</span>
                      </div>
                      <p className="text-xl font-bold text-purple-800">${(cashSession?.transfer_sales || 0).toFixed(2)}</p>
                    </div>
                  </div>
                  
                  <div className="p-3 bg-yellow-50 rounded-lg flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-yellow-700">Haz clic en &quot;Iniciar Cierre&quot; para ver el resumen detallado</p>
                  </div>
                  <Button 
                    variant="destructive" 
                    className="w-full"
                    onClick={loadPreCloseSummary}
                    disabled={loadingPreClose}
                    data-testid="start-close-btn"
                  >
                    {loadingPreClose ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Lock className="w-4 h-4 mr-2" />
                    )}
                    Iniciar Cierre de Caja
                  </Button>
                </CardContent>
              </Card>

              {/* History */}
              {history.length > 0 && (
                <Card className="border border-border/50">
                  <CardHeader>
                    <CardTitle className="font-chivo">Historial de Cortes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Fecha de Negocio</TableHead>
                          <TableHead>Apertura</TableHead>
                          <TableHead>Cierre</TableHead>
                          <TableHead className="text-right">Ventas</TableHead>
                          <TableHead className="text-right">Diferencia</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {history.slice(0, 10).map((session, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium text-accent">
                              {session.business_date ? 
                                new Date(session.business_date + 'T12:00:00').toLocaleDateString('es-MX', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }) 
                                : new Date(session.opened_at).toLocaleDateString('es-MX', { timeZone: 'America/Hermosillo' })}
                            </TableCell>
                            <TableCell>${session.initial_amount.toFixed(2)}</TableCell>
                            <TableCell>${session.final_amount?.toFixed(2) || '-'}</TableCell>
                            <TableCell className="text-right text-green-600">
                              ${session.total_sales.toFixed(2)}
                            </TableCell>
                            <TableCell className={`text-right font-bold ${session.difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {session.difference >= 0 ? '+' : ''}${session.difference?.toFixed(2) || '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
          </>
          ) : activeTab === 'ventas' ? (
            /* NEW: Ventas Tab - Professional FUDO-style Design */
            <div className="flex flex-col h-[calc(100vh-220px)]">
              {/* Filters Bar */}
              <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex-shrink-0">
                <div className="flex flex-wrap items-center gap-3">
                  {/* Date Filters */}
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <input
                      type="date"
                      value={salesFilters.startDate}
                      onChange={(e) => setSalesFilters(prev => ({ ...prev, startDate: e.target.value }))}
                      className="border rounded-lg px-3 py-1.5 text-sm w-36"
                      data-testid="sales-start-date"
                    />
                    <span className="text-slate-400">-</span>
                    <input
                      type="date"
                      value={salesFilters.endDate}
                      onChange={(e) => setSalesFilters(prev => ({ ...prev, endDate: e.target.value }))}
                      className="border rounded-lg px-3 py-1.5 text-sm w-36"
                      data-testid="sales-end-date"
                    />
                  </div>
                  
                  {/* Quick Date Buttons */}
                  <div className="flex gap-1">
                    <button
                      onClick={() => {
                        const today = getLocalDateString();
                        setSalesFilters(prev => ({ ...prev, startDate: today, endDate: today }));
                      }}
                      className="px-3 py-1.5 text-xs font-medium bg-accent text-white rounded-lg hover:bg-accent/90"
                    >
                      Hoy
                    </button>
                    <button
                      onClick={() => {
                        const today = new Date();
                        const weekAgo = new Date(today);
                        weekAgo.setDate(today.getDate() - 7);
                        setSalesFilters(prev => ({ ...prev, startDate: getLocalDateString(weekAgo), endDate: getLocalDateString() }));
                      }}
                      className="px-3 py-1.5 text-xs font-medium bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
                    >
                      7 Días
                    </button>
                    <button
                      onClick={() => {
                        const today = new Date();
                        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
                        setSalesFilters(prev => ({ ...prev, startDate: getLocalDateString(monthStart), endDate: getLocalDateString() }));
                      }}
                      className="px-3 py-1.5 text-xs font-medium bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
                    >
                      Este Mes
                    </button>
                  </div>
                  
                  {/* Divider */}
                  <div className="h-8 w-px bg-slate-200" />
                  
                  {/* Type Filter */}
                  <select
                    value={salesFilters.orderType}
                    onChange={(e) => setSalesFilters(prev => ({ ...prev, orderType: e.target.value }))}
                    className="border rounded-lg px-3 py-1.5 text-sm bg-white"
                    data-testid="sales-type-filter"
                  >
                    <option value="">Todos los tipos</option>
                    <option value="dine_in">Mesa</option>
                    <option value="takeout">Para Llevar</option>
                    <option value="delivery">Domicilio</option>
                  </select>
                  
                  {/* Payment Filter */}
                  <select
                    value={salesFilters.paymentMethod}
                    onChange={(e) => setSalesFilters(prev => ({ ...prev, paymentMethod: e.target.value }))}
                    className="border rounded-lg px-3 py-1.5 text-sm bg-white"
                    data-testid="sales-payment-filter"
                  >
                    <option value="">Todos los pagos</option>
                    <option value="efectivo">Efectivo</option>
                    <option value="tarjeta">Tarjeta</option>
                    <option value="transferencia">Transferencia</option>
                  </select>
                  
                  {/* Status Filter */}
                  <select
                    value={salesFilters.status}
                    onChange={(e) => setSalesFilters(prev => ({ ...prev, status: e.target.value }))}
                    className="border rounded-lg px-3 py-1.5 text-sm bg-white"
                    data-testid="sales-status-filter"
                  >
                    <option value="">Completadas + Anuladas</option>
                    <option value="completed">Solo Completadas</option>
                    <option value="voided">Solo Anuladas</option>
                    <option value="all">Todas</option>
                  </select>
                  
                  {/* Search */}
                  <div className="relative flex-1 min-w-[200px]">
                    <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="text"
                      placeholder="Buscar cliente, teléfono, # orden..."
                      value={salesFilters.search}
                      onChange={(e) => handleSalesSearch(e.target.value)}
                      className="pl-9 h-9 text-sm"
                      data-testid="sales-search-input"
                    />
                    {salesFilters.search && (
                      <button
                        onClick={() => { setSalesFilters(prev => ({ ...prev, search: '' })); fetchSalesOrders(); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded"
                      >
                        <X className="w-3 h-3 text-slate-400" />
                      </button>
                    )}
                  </div>
                  
                  {/* Refresh Button */}
                  <Button variant="outline" size="icon" onClick={fetchSalesOrders} className="h-9 w-9">
                    <RefreshCw className={`w-4 h-4 ${loadingSales ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>

              {/* Summary Dashboard */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4 flex-shrink-0">
                <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <DollarSign className="w-8 h-8 opacity-70" />
                    <span className="text-xs opacity-70">{salesOrders.period?.label}</span>
                  </div>
                  <p className="text-2xl font-bold mt-2">${salesOrders.summary?.total_sales?.toLocaleString() || '0'}</p>
                  <p className="text-xs opacity-80">Total Ventas</p>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-blue-600">
                    <Receipt className="w-5 h-5" />
                    <span className="text-xl font-bold">{salesOrders.summary?.total_orders || 0}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Órdenes</p>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-purple-600">
                    <TrendingUp className="w-5 h-5" />
                    <span className="text-xl font-bold">${salesOrders.summary?.avg_ticket?.toFixed(0) || '0'}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Ticket Promedio</p>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-teal-600">
                    <Users className="w-5 h-5" />
                    <span className="text-xl font-bold">{salesOrders.summary?.total_guests || 0}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Comensales</p>
                </div>
                {salesOrders.summary?.total_voided > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-red-600">
                      <Ban className="w-5 h-5" />
                      <span className="text-xl font-bold">{salesOrders.summary?.total_voided || 0}</span>
                    </div>
                    <p className="text-xs text-red-500 mt-1">Anuladas (${salesOrders.summary?.total_voided_amount?.toFixed(0)})</p>
                  </div>
                )}
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-emerald-600">
                    <TrendingUp className="w-5 h-5" />
                    <span className="text-xl font-bold">${salesOrders.summary?.total_profit?.toLocaleString() || '0'}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Ganancia ({salesOrders.summary?.profit_margin}%)</p>
                </div>
              </div>

              {/* Two Panel Layout */}
              <div className="flex gap-4 flex-1 min-h-0">
                {/* Left Panel: Orders List */}
                <div className={`flex flex-col transition-all duration-300 ${selectedSaleOrder ? 'w-1/2' : 'w-full'}`}>
                  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col flex-1">
                    {/* Header */}
                    <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between flex-shrink-0">
                      <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                        <ShoppingCart className="w-4 h-4" />
                        Órdenes ({salesOrders.orders?.length || 0})
                      </h3>
                      {/* Payment Method Summary Pills */}
                      <div className="flex items-center gap-2">
                        {Object.entries(salesOrders.by_payment_method || {}).map(([method, data]) => (
                          <div key={method} className="flex items-center gap-1 px-2 py-1 bg-slate-100 rounded-full text-xs">
                            {method === 'efectivo' ? <DollarSign className="w-3 h-3 text-green-600" /> :
                             method === 'tarjeta' ? <CreditCard className="w-3 h-3 text-blue-600" /> :
                             <Receipt className="w-3 h-3 text-purple-600" />}
                            <span className="font-medium">{data.count}</span>
                            <span className="text-slate-500">(${(data.amount/1000).toFixed(1)}k)</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Orders List */}
                    <div className="flex-1 overflow-y-auto">
                      {loadingSales ? (
                        <div className="flex items-center justify-center py-20">
                          <div className="animate-spin w-8 h-8 border-4 border-accent border-t-transparent rounded-full" />
                        </div>
                      ) : salesOrders.orders?.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                          <ShoppingCart className="w-16 h-16 mb-4 opacity-30" />
                          <p className="text-lg font-medium">No hay ventas</p>
                          <p className="text-sm">Ajusta los filtros o selecciona otro período</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-slate-100">
                          {salesOrders.orders?.map((order, idx) => {
                            const isSelected = selectedSaleOrder?.order_id === order.order_id;
                            const isVoided = order.status === 'voided';
                            
                            return (
                              <div
                                key={order.order_id}
                                onClick={() => setSelectedSaleOrder(isSelected ? null : order)}
                                className={`flex items-center gap-4 p-4 cursor-pointer transition-all ${
                                  isSelected 
                                    ? 'bg-accent/10 border-l-4 border-l-accent' 
                                    : isVoided
                                    ? 'bg-red-50/50 border-l-4 border-l-red-400 hover:bg-red-50'
                                    : 'hover:bg-slate-50 border-l-4 border-l-transparent'
                                }`}
                                data-testid={`order-row-${order.order_number}`}
                              >
                                {/* Order Number & Type */}
                                <div className="flex-shrink-0 w-20">
                                  <span className={`font-mono font-bold text-lg ${isVoided ? 'text-red-500 line-through' : 'text-accent'}`}>
                                    #{order.order_number}
                                  </span>
                                  <div className="flex items-center gap-1 mt-1">
                                    {order.order_type === 'dine_in' ? (
                                      <span className="flex items-center gap-1 text-[10px] text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">
                                        <Utensils className="w-3 h-3" /> M{order.table_number}
                                      </span>
                                    ) : order.order_type === 'takeout' ? (
                                      <span className="flex items-center gap-1 text-[10px] text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded">
                                        <Package className="w-3 h-3" /> Llevar
                                      </span>
                                    ) : (
                                      <span className="flex items-center gap-1 text-[10px] text-purple-600 bg-purple-100 px-1.5 py-0.5 rounded">
                                        <Truck className="w-3 h-3" /> Envío
                                      </span>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Customer Info */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className={`font-semibold truncate ${isVoided ? 'text-red-700' : 'text-slate-800'}`}>
                                      {order.customer_name}
                                    </p>
                                    {isVoided && (
                                      <span className="px-2 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded">ANULADA</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                                    {/* Show date if different from filter or range > 1 day */}
                                    {(salesFilters.startDate !== salesFilters.endDate || order.date) && (
                                      <span className="flex items-center gap-1 text-slate-600 font-medium">
                                        <Calendar className="w-3 h-3" />
                                        {order.date}
                                      </span>
                                    )}
                                    <span className="flex items-center gap-1">
                                      <Clock className="w-3 h-3" />
                                      {order.time}
                                    </span>
                                    <span className="flex items-center gap-1">
                                      <Package className="w-3 h-3" />
                                      {order.items?.length} items
                                    </span>
                                    {order.customer_phone && (
                                      <span className="flex items-center gap-1">
                                        <Phone className="w-3 h-3" />
                                        {order.customer_phone}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Payment & Total */}
                                <div className="flex-shrink-0 text-right">
                                  <p className={`text-lg font-bold ${isVoided ? 'text-red-500 line-through' : 'text-green-600'}`}>
                                    ${order.total?.toFixed(2)}
                                  </p>
                                  <div className="flex items-center justify-end gap-1 mt-1">
                                    {order.payment_method === 'efectivo' ? (
                                      <span className="flex items-center gap-1 text-[10px] text-green-700 bg-green-100 px-1.5 py-0.5 rounded">
                                        <DollarSign className="w-3 h-3" /> Efectivo
                                      </span>
                                    ) : order.payment_method === 'tarjeta' ? (
                                      <span className="flex items-center gap-1 text-[10px] text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">
                                        <CreditCard className="w-3 h-3" /> Tarjeta
                                      </span>
                                    ) : (
                                      <span className="flex items-center gap-1 text-[10px] text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded">
                                        <Receipt className="w-3 h-3" /> Transfer
                                      </span>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Chevron */}
                                <ChevronRight className={`w-5 h-5 text-slate-300 transition-transform ${isSelected ? 'rotate-180' : ''}`} />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right Panel: Order Detail */}
                {selectedSaleOrder && (
                  <div className="w-1/2 bg-slate-800 rounded-xl text-white overflow-hidden flex flex-col">
                    {/* Panel Header */}
                    <div className={`p-4 flex items-center justify-between flex-shrink-0 ${selectedSaleOrder.status === 'voided' ? 'bg-red-900' : 'bg-slate-900'}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold ${selectedSaleOrder.status === 'voided' ? 'bg-red-700' : 'bg-accent'}`}>
                          #{selectedSaleOrder.order_number}
                        </div>
                        <div>
                          <h3 className="font-bold text-lg">{selectedSaleOrder.customer_name}</h3>
                          <div className="flex items-center gap-2 text-sm text-slate-400">
                            {selectedSaleOrder.customer_phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {selectedSaleOrder.customer_phone}
                              </span>
                            )}
                            <span>{selectedSaleOrder.date} • {selectedSaleOrder.time}</span>
                          </div>
                        </div>
                      </div>
                      <button 
                        onClick={() => setSelectedSaleOrder(null)}
                        className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                        data-testid="close-order-panel"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Void Warning */}
                    {selectedSaleOrder.status === 'voided' && (
                      <div className="bg-red-800/50 px-4 py-3 flex-shrink-0">
                        <div className="flex items-center gap-2 text-red-200">
                          <Ban className="w-5 h-5" />
                          <div>
                            <p className="font-semibold">Orden Anulada</p>
                            <p className="text-sm opacity-80">Motivo: {selectedSaleOrder.void_reason || 'Sin especificar'}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Order Stats */}
                    <div className="grid grid-cols-3 gap-3 p-4 bg-slate-900/50 flex-shrink-0">
                      <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                        <p className={`text-2xl font-bold ${selectedSaleOrder.status === 'voided' ? 'text-red-400 line-through' : 'text-green-400'}`}>
                          ${selectedSaleOrder.total?.toFixed(2)}
                        </p>
                        <p className="text-xs text-slate-400">Total</p>
                      </div>
                      <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-blue-400">{selectedSaleOrder.items?.length || 0}</p>
                        <p className="text-xs text-slate-400">Productos</p>
                      </div>
                      <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-purple-400">{selectedSaleOrder.num_guests || 1}</p>
                        <p className="text-xs text-slate-400">Comensales</p>
                      </div>
                    </div>

                    {/* Order Info Row */}
                    <div className="flex items-center gap-3 px-4 py-2 bg-slate-900/30 text-sm flex-shrink-0">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        selectedSaleOrder.order_type === 'dine_in' ? 'bg-blue-500/30 text-blue-300' :
                        selectedSaleOrder.order_type === 'takeout' ? 'bg-amber-500/30 text-amber-300' :
                        'bg-purple-500/30 text-purple-300'
                      }`}>
                        {selectedSaleOrder.order_type === 'dine_in' ? `Mesa ${selectedSaleOrder.table_number || ''}` : 
                         selectedSaleOrder.order_type === 'takeout' ? 'Para Llevar' : 'Domicilio'}
                      </span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        selectedSaleOrder.payment_method === 'efectivo' ? 'bg-green-500/30 text-green-300' :
                        selectedSaleOrder.payment_method === 'tarjeta' ? 'bg-blue-500/30 text-blue-300' :
                        'bg-purple-500/30 text-purple-300'
                      }`}>
                        {selectedSaleOrder.payment_method === 'efectivo' ? 'Efectivo' :
                         selectedSaleOrder.payment_method === 'tarjeta' ? 'Tarjeta' : 'Transferencia'}
                      </span>
                      {selectedSaleOrder.discount_amount > 0 && (
                        <span className="px-2 py-1 rounded text-xs font-medium bg-orange-500/30 text-orange-300">
                          Descuento: ${selectedSaleOrder.discount_amount}
                        </span>
                      )}
                    </div>

                    {/* Items List */}
                    <div className="flex-1 overflow-y-auto p-4">
                      <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        Productos Ordenados
                      </h4>
                      <div className="space-y-2">
                        {selectedSaleOrder.items?.map((item, idx) => {
                          const isCancelled = item.item_status === 'cancelled';
                          return (
                            <div key={idx} className={`rounded-lg p-3 ${isCancelled ? 'bg-red-900/30 border border-red-500/30' : 'bg-slate-700/50'}`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className={`font-bold ${isCancelled ? 'text-red-400' : 'text-accent'}`}>{item.quantity}x</span>
                                  <span className={isCancelled ? 'text-red-300 line-through' : 'text-white'}>{item.product_name}</span>
                                  {isCancelled && <span className="text-[10px] bg-red-500 text-white px-1.5 py-0.5 rounded">CANCELADO</span>}
                                </div>
                                <span className={`font-bold ${isCancelled ? 'text-red-400 line-through' : 'text-green-400'}`}>
                                  ${item.subtotal?.toFixed(2)}
                                </span>
                              </div>
                              {item.notes && (
                                <p className="text-xs text-slate-400 mt-1 pl-6">Nota: {item.notes}</p>
                              )}
                              {isCancelled && item.cancellation_reason && (
                                <p className="text-xs text-red-400 mt-1 pl-6">Motivo: {item.cancellation_reason}</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Comments */}
                      {selectedSaleOrder.comments && (
                        <div className="mt-4 p-3 bg-slate-700/30 rounded-lg">
                          <p className="text-xs text-slate-400 mb-1">Comentarios:</p>
                          <p className="text-sm text-slate-200">{selectedSaleOrder.comments}</p>
                        </div>
                      )}
                      
                      {/* Delivery Address */}
                      {selectedSaleOrder.order_type === 'delivery' && selectedSaleOrder.customer_address && (
                        <div className="mt-4 p-3 bg-purple-900/30 rounded-lg border border-purple-500/30">
                          <p className="text-xs text-purple-300 mb-1 flex items-center gap-1">
                            <Truck className="w-3 h-3" /> Dirección de Entrega:
                          </p>
                          <p className="text-sm text-white">{selectedSaleOrder.customer_address}</p>
                        </div>
                      )}
                    </div>

                    {/* Action Footer */}
                    {isAdmin && selectedSaleOrder.status !== 'voided' && (
                      <div className="p-4 bg-slate-900 flex items-center gap-3 flex-shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditModal(selectedSaleOrder)}
                          className="flex-1 bg-transparent border-slate-600 text-white hover:bg-slate-700"
                          data-testid="edit-selected-order"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Editar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openVoidModal(selectedSaleOrder)}
                          className="flex-1 bg-transparent border-red-600 text-red-400 hover:bg-red-900/50"
                          data-testid="void-selected-order"
                        >
                          <Ban className="w-4 h-4 mr-2" />
                          Anular
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => fetchAuditLog(selectedSaleOrder.order_id)}
                          className="bg-transparent border-slate-600 text-white hover:bg-slate-700"
                          data-testid="audit-selected-order"
                        >
                          <History className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : activeTab === 'estadisticas' ? (
            /* Estadísticas Tab - Reportes por período */
            <div className="space-y-6">
              {/* Period Selector */}
              <Card className="border border-border/50">
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    {/* Period Type Selector */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Ver por:</span>
                      <div className="flex bg-gray-100 rounded-lg p-1">
                        {[
                          { key: 'day', label: 'Día' },
                          { key: 'week', label: 'Semana' },
                          { key: 'month', label: 'Mes' },
                          { key: 'year', label: 'Año' }
                        ].map(p => (
                          <button
                            key={p.key}
                            onClick={() => setSelectedPeriod(p.key)}
                            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                              selectedPeriod === p.key ? 'bg-white shadow text-accent' : 'text-gray-600 hover:text-gray-900'
                            }`}
                          >
                            {p.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Date Navigation */}
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => navigatePeriod(-1)}
                        className="p-2 hover:bg-gray-100 rounded-lg"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        <input
                          type="date"
                          value={selectedDate}
                          onChange={(e) => setSelectedDate(e.target.value)}
                          className="border rounded-lg px-3 py-1.5 text-sm"
                        />
                      </div>
                      <button 
                        onClick={() => navigatePeriod(1)}
                        className="p-2 hover:bg-gray-100 rounded-lg"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => setSelectedDate(getLocalDateString())}
                        className="px-3 py-1.5 text-sm bg-accent text-white rounded-lg hover:bg-accent/90"
                      >
                        Hoy
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {loadingStats ? (
                <div className="flex items-center justify-center py-20">
                  <div className="animate-spin w-8 h-8 border-4 border-accent border-t-transparent rounded-full" />
                </div>
              ) : periodStats ? (
                <>
                  {/* Period Header */}
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold">{periodStats.period_label}</h2>
                    <span className="text-sm text-muted-foreground">
                      {periodStats.summary?.total_orders || 0} órdenes
                    </span>
                  </div>

                  {/* Summary Cards with Comparison */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="border border-green-200 bg-green-50">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <DollarSign className="w-8 h-8 text-green-600" />
                          <div>
                            <p className="text-xs text-green-700">Total Ventas</p>
                            <p className="text-2xl font-bold text-green-700">
                              ${periodStats.summary?.total_amount?.toLocaleString() || '0'}
                            </p>
                            {periodStats.comparison && (
                              <p className={`text-xs flex items-center gap-1 ${periodStats.comparison.sales_change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {periodStats.comparison.sales_change >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                                {periodStats.comparison.sales_change >= 0 ? '+' : ''}{periodStats.comparison.sales_change}%
                              </p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border border-blue-200 bg-blue-50">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <TrendingUp className="w-8 h-8 text-blue-600" />
                          <div>
                            <p className="text-xs text-blue-700">Ganancia</p>
                            <p className="text-2xl font-bold text-blue-700">
                              ${periodStats.summary?.total_profit?.toLocaleString() || '0'}
                            </p>
                            {periodStats.comparison && (
                              <p className={`text-xs flex items-center gap-1 ${periodStats.comparison.profit_change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {periodStats.comparison.profit_change >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                                {periodStats.comparison.profit_change >= 0 ? '+' : ''}{periodStats.comparison.profit_change}%
                              </p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border border-purple-200 bg-purple-50">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <Receipt className="w-8 h-8 text-purple-600" />
                          <div>
                            <p className="text-xs text-purple-700">Ticket Promedio</p>
                            <p className="text-2xl font-bold text-purple-700">
                              ${periodStats.summary?.avg_ticket?.toFixed(0) || '0'}
                            </p>
                            {periodStats.comparison && (
                              <p className={`text-xs flex items-center gap-1 ${periodStats.comparison.avg_ticket_change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {periodStats.comparison.avg_ticket_change >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                                {periodStats.comparison.avg_ticket_change >= 0 ? '+' : ''}{periodStats.comparison.avg_ticket_change}%
                              </p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border border-orange-200 bg-orange-50">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <Users className="w-8 h-8 text-orange-600" />
                          <div>
                            <p className="text-xs text-orange-700">Clientes</p>
                            <p className="text-2xl font-bold text-orange-700">
                              {periodStats.summary?.total_guests || 0}
                            </p>
                            {periodStats.comparison && (
                              <p className={`text-xs flex items-center gap-1 ${periodStats.comparison.guests_change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {periodStats.comparison.guests_change >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                                {periodStats.comparison.guests_change >= 0 ? '+' : ''}{periodStats.comparison.guests_change}%
                              </p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Comparison Summary Banner */}
                  {periodStats.comparison && (
                    <Card className="border border-slate-200 bg-slate-50">
                      <CardContent className="p-4">
                        <div className="flex flex-wrap items-center justify-between gap-4">
                          <div className="flex items-center gap-2">
                            <BarChart3 className="w-5 h-5 text-slate-600" />
                            <span className="text-sm font-medium text-slate-700">
                              Comparación con: <span className="font-bold">{periodStats.comparison.previous_period_label}</span>
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-4 text-sm">
                            <div className="flex items-center gap-1">
                              <span className="text-slate-500">Ventas ant:</span>
                              <span className="font-medium">${periodStats.comparison.previous_summary?.total_amount?.toLocaleString() || 0}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-slate-500">Órdenes ant:</span>
                              <span className="font-medium">{periodStats.comparison.previous_summary?.total_orders || 0}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-slate-500">Ganancia ant:</span>
                              <span className="font-medium">${periodStats.comparison.previous_summary?.total_profit?.toLocaleString() || 0}</span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Detailed Data */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Comparative Chart */}
                    <Card className="lg:col-span-2 border border-border/50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                          <BarChart3 className="w-4 h-4 text-accent" />
                          Comparativo de Ventas
                        </CardTitle>
                        {periodStats.comparison && (
                          <div className="flex items-center gap-4 mt-2 text-xs">
                            <div className="flex items-center gap-1.5">
                              <div className="w-3 h-3 rounded bg-accent" />
                              <span>{periodStats.period_label}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <div className="w-3 h-3 rounded bg-slate-400" />
                              <span>{periodStats.comparison.previous_period_label}</span>
                            </div>
                          </div>
                        )}
                      </CardHeader>
                      <CardContent>
                        {periodStats.data?.length === 0 && (!periodStats.comparison?.previous_data || periodStats.comparison.previous_data.length === 0) ? (
                          <p className="text-center text-muted-foreground py-8">Sin ventas en estos períodos</p>
                        ) : (
                          <div className="space-y-3 max-h-96 overflow-y-auto">
                            {(() => {
                              // Merge current and previous period data by label
                              const allLabels = new Set();
                              const currentMap = {};
                              const previousMap = {};
                              
                              periodStats.data?.forEach(item => {
                                allLabels.add(item.label);
                                currentMap[item.label] = item;
                              });
                              
                              periodStats.comparison?.previous_data?.forEach(item => {
                                allLabels.add(item.label);
                                previousMap[item.label] = item;
                              });
                              
                              const labels = Array.from(allLabels).sort();
                              const allAmounts = [
                                ...periodStats.data?.map(d => d.total_amount) || [],
                                ...periodStats.comparison?.previous_data?.map(d => d.total_amount) || []
                              ];
                              const maxAmount = Math.max(...allAmounts, 1);
                              
                              return labels.map((label, idx) => {
                                const current = currentMap[label];
                                const previous = previousMap[label];
                                const currentPercent = current ? (current.total_amount / maxAmount) * 100 : 0;
                                const prevPercent = previous ? (previous.total_amount / maxAmount) * 100 : 0;
                                
                                return (
                                  <div key={idx} className="space-y-1">
                                    <span className="text-xs text-muted-foreground font-mono">{label}</span>
                                    <div className="space-y-1">
                                      {/* Current period bar */}
                                      <div className="flex items-center gap-2">
                                        <div className="flex-1 h-6 bg-gray-100 rounded overflow-hidden relative">
                                          <div 
                                            className="h-full bg-gradient-to-r from-accent to-accent/70 rounded transition-all duration-500"
                                            style={{ width: `${currentPercent}%` }}
                                          />
                                          <span className="absolute inset-0 flex items-center px-2 text-xs font-medium">
                                            ${current?.total_amount?.toLocaleString() || 0}
                                          </span>
                                        </div>
                                        <span className="text-xs text-muted-foreground w-16 text-right">
                                          {current?.orders_count || 0} ord
                                        </span>
                                      </div>
                                      {/* Previous period bar */}
                                      {periodStats.comparison && (
                                        <div className="flex items-center gap-2">
                                          <div className="flex-1 h-5 bg-gray-50 rounded overflow-hidden relative">
                                            <div 
                                              className="h-full bg-slate-400 rounded transition-all duration-500 opacity-70"
                                              style={{ width: `${prevPercent}%` }}
                                            />
                                            <span className="absolute inset-0 flex items-center px-2 text-xs text-slate-600">
                                              ${previous?.total_amount?.toLocaleString() || 0}
                                            </span>
                                          </div>
                                          <span className="text-xs text-slate-400 w-16 text-right">
                                            {previous?.orders_count || 0} ord
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Right Column */}
                    <div className="space-y-4">
                      {/* Top Products */}
                      <Card className="border border-border/50">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-medium flex items-center gap-2">
                            <Package className="w-4 h-4 text-accent" />
                            Productos Más Vendidos
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2 max-h-48 overflow-y-auto">
                            {periodStats.top_products?.slice(0, 5).map((prod, idx) => (
                              <div key={idx} className="flex items-center justify-between text-sm">
                                <div className="flex items-center gap-2">
                                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                                    idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                                    idx === 1 ? 'bg-gray-300 text-gray-700' :
                                    idx === 2 ? 'bg-orange-300 text-orange-800' :
                                    'bg-gray-100 text-gray-600'
                                  }`}>
                                    {idx + 1}
                                  </span>
                                  <span className="truncate max-w-32">{prod.name}</span>
                                </div>
                                <div className="text-right">
                                  <span className="font-medium">{prod.quantity}</span>
                                  <span className="text-xs text-muted-foreground ml-1">uds</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>

                      {/* By Order Type */}
                      <Card className="border border-border/50">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-medium flex items-center gap-2">
                            <PieChart className="w-4 h-4 text-accent" />
                            Por Tipo de Orden
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            {Object.entries(periodStats.by_order_type || {}).map(([type, data]) => {
                              const icons = {
                                dine_in: <Utensils className="w-4 h-4 text-blue-500" />,
                                takeout: <Package className="w-4 h-4 text-green-500" />,
                                delivery: <Truck className="w-4 h-4 text-orange-500" />
                              };
                              const labels = {
                                dine_in: 'Mesa',
                                takeout: 'Para llevar',
                                delivery: 'Domicilio'
                              };
                              return (
                                <div key={type} className="flex items-center justify-between text-sm">
                                  <div className="flex items-center gap-2">
                                    {icons[type] || <Receipt className="w-4 h-4" />}
                                    <span>{labels[type] || type}</span>
                                  </div>
                                  <div className="text-right">
                                    <span className="font-medium">${data.amount.toLocaleString()}</span>
                                    <span className="text-xs text-muted-foreground ml-1">({data.count})</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </CardContent>
                      </Card>

                      {/* By Payment Method */}
                      <Card className="border border-border/50">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-medium flex items-center gap-2">
                            <CreditCard className="w-4 h-4 text-accent" />
                            Por Método de Pago
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            {Object.entries(periodStats.by_payment_method || {}).map(([method, data]) => {
                              const icons = {
                                efectivo: <DollarSign className="w-4 h-4 text-green-500" />,
                                tarjeta: <CreditCard className="w-4 h-4 text-blue-500" />,
                                transferencia: <ArrowUpDown className="w-4 h-4 text-purple-500" />
                              };
                              const labels = {
                                efectivo: 'Efectivo',
                                tarjeta: 'Tarjeta',
                                transferencia: 'Transferencia'
                              };
                              return (
                                <div key={method} className="flex items-center justify-between text-sm">
                                  <div className="flex items-center gap-2">
                                    {icons[method] || <DollarSign className="w-4 h-4" />}
                                    <span>{labels[method] || method}</span>
                                  </div>
                                  <div className="text-right">
                                    <span className="font-medium">${data.amount.toLocaleString()}</span>
                                    <span className="text-xs text-muted-foreground ml-1">({data.count})</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-center text-muted-foreground py-8">Selecciona un período para ver las estadísticas</p>
              )}
            </div>
          ) : activeTab === 'movimientos' ? (
            /* Movimientos Tab */
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="border border-green-200 bg-green-50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <TrendingUp className="w-6 h-6 text-green-600" />
                      <div>
                        <p className="text-xs text-green-700">Total Ingresos</p>
                        <p className="text-2xl font-bold text-green-700">${allMovements.summary?.total_in?.toFixed(2) || '0.00'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border border-red-200 bg-red-50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <TrendingDown className="w-6 h-6 text-red-600" />
                      <div>
                        <p className="text-xs text-red-700">Total Egresos</p>
                        <p className="text-2xl font-bold text-red-700">${allMovements.summary?.total_out?.toFixed(2) || '0.00'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border border-accent bg-accent/5">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Wallet className="w-6 h-6 text-accent" />
                      <div>
                        <p className="text-xs text-muted-foreground">Balance Total</p>
                        <p className="text-2xl font-bold text-accent">${allMovements.summary?.balance?.toFixed(2) || '0.00'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Movements Table */}
              <Card className="border border-border/50">
                <CardHeader>
                  <CardTitle className="font-chivo flex items-center gap-2">
                    <ArrowUpDown className="w-5 h-5" />
                    Historial de Movimientos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Fecha</TableHead>
                        <TableHead className="text-right">Monto</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Motivo</TableHead>
                        <TableHead>Medio de Pago</TableHead>
                        <TableHead>Creado Por</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {allMovements.movements?.map((mov, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="text-sm">
                            {mov.created_at ? new Date(mov.created_at).toLocaleString('es-MX', {
                              timeZone: 'America/Hermosillo',
                              day: '2-digit', month: '2-digit', year: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            }) : '-'}
                          </TableCell>
                          <TableCell className={`text-right font-bold ${
                            mov.movement_type === 'in' || mov.movement_type === 'sale' 
                              ? 'text-green-600' 
                              : 'text-red-600'
                          }`}>
                            {mov.movement_type === 'in' || mov.movement_type === 'sale' ? '+' : '-'}${mov.amount?.toFixed(2)}
                          </TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              mov.movement_type === 'in' ? 'bg-green-100 text-green-700' : 
                              mov.movement_type === 'sale' ? 'bg-blue-100 text-blue-700' :
                              mov.movement_type === 'cash_out' && mov.reference_type === 'purchase' ? 'bg-orange-100 text-orange-700' :
                              mov.movement_type === 'cash_out' && mov.reference_type === 'expense' ? 'bg-purple-100 text-purple-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {mov.movement_type === 'in' ? 'Ingreso' : 
                               mov.movement_type === 'sale' ? 'Venta' :
                               mov.movement_type === 'cash_out' && mov.reference_type === 'purchase' ? 'Compra' :
                               mov.movement_type === 'cash_out' && mov.reference_type === 'expense' ? 'Gasto Op.' :
                               mov.movement_type === 'out' || mov.movement_type === 'cash_out' ? 'Egreso' : 'Egreso'}
                            </span>
                          </TableCell>
                          <TableCell>{mov.reason || '-'}</TableCell>
                          <TableCell>{mov.payment_method || 'Efectivo'}</TableCell>
                          <TableCell>{mov.created_by || 'Sistema'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {allMovements.movements?.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">No hay movimientos registrados</p>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : activeTab === 'arqueos' ? (
            /* Arqueos Tab - with Side Panel */
            <div className="flex gap-6">
              {/* Left: Arqueos List */}
              <div className={`space-y-6 transition-all duration-300 ${selectedArqueo ? 'w-1/2' : 'w-full'}`}>
                {/* Current Session Info */}
                {cashSession && (
                  <Card className="border-green-500 bg-green-50">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                            <Unlock className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="font-bold">Caja Actualmente Abierta</p>
                            <p className="text-sm text-muted-foreground">
                              Saldo actual: ${expectedTotal.toFixed(2)}
                            </p>
                          </div>
                        </div>
                        {!selectedArqueo && (
                          <div className="text-right">
                            <p className="text-sm text-muted-foreground">Abierta desde</p>
                            <p className="font-medium">{cashSession?.opened_at ? formatDateTime(cashSession.opened_at) : '-'}</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Arqueos List */}
                <Card className="border border-border/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="font-chivo flex items-center gap-2 text-base">
                      <FileText className="w-5 h-5" />
                      Historial de Arqueos
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y">
                      {arqueos.map((arqueo, idx) => {
                        const isSelected = selectedArqueo?.session_id === arqueo.session_id;
                        
                        return (
                          <div
                            key={idx}
                            onClick={() => fetchArqueoDetail(arqueo)}
                            className={`p-4 cursor-pointer transition-all hover:bg-gray-50 ${
                              isSelected ? 'bg-accent/10 border-l-4 border-l-accent' : ''
                            }`}
                            data-testid={`arqueo-row-${idx}`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                  arqueo.status === 'open' ? 'bg-green-100' : 'bg-gray-100'
                                }`}>
                                  {arqueo.status === 'open' ? (
                                    <Unlock className="w-5 h-5 text-green-600" />
                                  ) : (
                                    <Lock className="w-5 h-5 text-gray-500" />
                                  )}
                                </div>
                                <div>
                                  <p className="font-medium text-accent">
                                    {arqueo.business_date ? 
                                      new Date(arqueo.business_date + 'T12:00:00').toLocaleDateString('es-MX', {
                                        day: '2-digit', month: 'short', year: 'numeric'
                                      }) 
                                      : (arqueo.opened_at ? new Date(arqueo.opened_at).toLocaleDateString('es-MX', {
                                        timeZone: 'America/Hermosillo',
                                        day: '2-digit', month: 'short', year: 'numeric'
                                      }) : '-')}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {arqueo.opened_by_name || 'Sistema'}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-green-600">${arqueo.total_sales?.toFixed(2) || '0.00'}</p>
                                <span className={`px-2 py-0.5 rounded-full text-xs ${
                                  arqueo.status === 'open' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {arqueo.status === 'open' ? 'Abierta' : 'Cerrada'}
                                </span>
                              </div>
                            </div>
                            {!selectedArqueo && (
                              <div className="flex items-center gap-4 text-xs text-muted-foreground mt-2">
                                <span>Inicial: ${arqueo.initial_amount?.toFixed(2)}</span>
                                <span className="text-green-600">+${arqueo.total_cash_in?.toFixed(2)}</span>
                                <span className="text-red-600">-${arqueo.total_cash_out?.toFixed(2)}</span>
                                {arqueo.status === 'closed' && (
                                  <span className={arqueo.difference === 0 ? 'text-green-600' : arqueo.difference > 0 ? 'text-blue-600' : 'text-red-600'}>
                                    Dif: {arqueo.difference >= 0 ? '+' : ''}${arqueo.difference?.toFixed(2)}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {arqueos.length === 0 && (
                      <p className="text-center text-muted-foreground py-8">No hay arqueos registrados</p>
                    )}
                  </CardContent>
                </Card>
              </div>
              
              {/* Right: Arqueo Detail Panel (FUDO style) */}
              {selectedArqueo && (
                <div className="w-1/2 bg-slate-800 rounded-xl text-white overflow-hidden flex flex-col max-h-[calc(100vh-200px)]">
                  {/* Panel Header */}
                  <div className="bg-slate-900 p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        selectedArqueo.status === 'open' ? 'bg-green-500' : 'bg-accent'
                      }`}>
                        {selectedArqueo.status === 'open' ? (
                          <Unlock className="w-6 h-6 text-white" />
                        ) : (
                          <FileText className="w-6 h-6 text-white" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-bold text-lg">Arqueo de Caja</h3>
                        <p className="text-amber-400 text-sm font-medium">
                          Fecha de Negocio: {selectedArqueo.business_date ? 
                            new Date(selectedArqueo.business_date + 'T12:00:00').toLocaleDateString('es-MX', {
                              weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
                            }) 
                            : (selectedArqueo.opened_at ? new Date(selectedArqueo.opened_at).toLocaleDateString('es-MX', {
                              timeZone: 'America/Hermosillo',
                              weekday: 'short', day: 'numeric', month: 'short'
                            }) : '-')}
                        </p>
                      </div>
                    </div>
                    <button 
                      onClick={() => setSelectedArqueo(null)}
                      className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {loadingDetail ? (
                    <div className="flex-1 flex items-center justify-center">
                      <div className="animate-spin w-8 h-8 border-4 border-accent border-t-transparent rounded-full" />
                    </div>
                  ) : (
                    <>
                      {/* Arqueo Stats */}
                      <div className="grid grid-cols-2 gap-3 p-4 bg-slate-900/50">
                        <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                          <p className="text-xl font-bold text-white">${arqueoDetail.session?.initial_amount?.toFixed(2) || '0.00'}</p>
                          <p className="text-xs text-slate-400">Inicial</p>
                        </div>
                        <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                          <p className="text-xl font-bold text-green-400">${arqueoDetail.session?.total_sales?.toFixed(2) || '0.00'}</p>
                          <p className="text-xs text-slate-400">Ventas</p>
                        </div>
                        <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                          <p className="text-xl font-bold">
                            <span className="text-green-400">+${arqueoDetail.session?.total_cash_in?.toFixed(2) || '0.00'}</span>
                            {' / '}
                            <span className="text-red-400">-${arqueoDetail.session?.total_cash_out?.toFixed(2) || '0.00'}</span>
                          </p>
                          <p className="text-xs text-slate-400">Entradas / Salidas</p>
                        </div>
                        <div className={`rounded-lg p-3 text-center ${
                          arqueoDetail.session?.difference === 0 ? 'bg-green-500/20' : 
                          arqueoDetail.session?.difference > 0 ? 'bg-blue-500/20' : 'bg-red-500/20'
                        }`}>
                          <p className={`text-xl font-bold ${
                            arqueoDetail.session?.difference === 0 ? 'text-green-400' : 
                            arqueoDetail.session?.difference > 0 ? 'text-blue-400' : 'text-red-400'
                          }`}>
                            {arqueoDetail.session?.difference >= 0 ? '+' : ''}${arqueoDetail.session?.difference?.toFixed(2) || '0.00'}
                          </p>
                          <p className="text-xs text-slate-400">Diferencia</p>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 overflow-y-auto p-4 space-y-4">
                        {/* Sales Section */}
                        <div>
                          <h4 className="text-sm font-medium text-slate-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                            <ShoppingCart className="w-4 h-4" />
                            Ventas ({arqueoDetail.sales?.length || 0})
                          </h4>
                          {arqueoDetail.sales?.length > 0 ? (
                            <div className="space-y-2">
                              {arqueoDetail.sales.map((sale, idx) => (
                                <div key={idx} className="bg-slate-700/50 rounded-lg p-3">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <span className="font-mono text-accent font-bold">#{sale.order_number}</span>
                                      <span className={`px-2 py-0.5 rounded text-xs ${
                                        sale.order_type === 'dine_in' ? 'bg-blue-500/20 text-blue-300' :
                                        sale.order_type === 'takeout' ? 'bg-yellow-500/20 text-yellow-300' :
                                        'bg-purple-500/20 text-purple-300'
                                      }`}>
                                        {sale.order_type === 'dine_in' ? 'Mesa' : 
                                         sale.order_type === 'takeout' ? 'P/Llevar' : 'Domicilio'}
                                      </span>
                                    </div>
                                    <span className="font-bold text-green-400">${sale.total?.toFixed(2)}</span>
                                  </div>
                                  <div className="flex items-center gap-3 text-xs text-slate-400">
                                    <span>{sale.customer_name || 'General'}</span>
                                    <span>•</span>
                                    <span>
                                      {sale.created_at ? new Date(sale.created_at).toLocaleTimeString('es-MX', {
                                        hour: '2-digit', minute: '2-digit'
                                      }) : '-'}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-slate-500 text-center py-3 bg-slate-700/30 rounded-lg text-sm">Sin ventas</p>
                          )}
                        </div>

                        {/* Movements Section */}
                        <div>
                          <h4 className="text-sm font-medium text-slate-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                            <ArrowUpDown className="w-4 h-4" />
                            Movimientos ({arqueoDetail.movements?.length || 0})
                          </h4>
                          {arqueoDetail.movements?.length > 0 ? (
                            <div className="space-y-2">
                              {arqueoDetail.movements.map((mov, idx) => (
                                <div key={idx} className="bg-slate-700/50 rounded-lg p-3 flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                      mov.movement_type === 'in' || mov.movement_type === 'sale' 
                                        ? 'bg-green-500/20' 
                                        : 'bg-red-500/20'
                                    }`}>
                                      {mov.movement_type === 'in' || mov.movement_type === 'sale' ? (
                                        <TrendingUp className="w-4 h-4 text-green-400" />
                                      ) : (
                                        <TrendingDown className="w-4 h-4 text-red-400" />
                                      )}
                                    </div>
                                    <div>
                                      <p className="text-sm">{mov.reason || 'Sin motivo'}</p>
                                      <p className="text-xs text-slate-400">
                                        {mov.created_at ? new Date(mov.created_at).toLocaleTimeString('es-MX', {
                                          hour: '2-digit', minute: '2-digit'
                                        }) : '-'}
                                        {mov.reference_type === 'purchase' && ' • Compra'}
                                        {mov.reference_type === 'expense' && ' • Gasto Op.'}
                                      </p>
                                    </div>
                                  </div>
                                  <span className={`font-bold ${
                                    mov.movement_type === 'in' || mov.movement_type === 'sale' 
                                      ? 'text-green-400' 
                                      : 'text-red-400'
                                  }`}>
                                    {mov.movement_type === 'in' || mov.movement_type === 'sale' ? '+' : '-'}${mov.amount?.toFixed(2)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-slate-500 text-center py-3 bg-slate-700/30 rounded-lg text-sm">Sin movimientos</p>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </div>
      </main>

      {/* ==================== EDIT ORDER MODAL ==================== */}
      {editModalOpen && selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
          >
            <div className="p-4 border-b bg-blue-500 text-white rounded-t-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Edit className="w-5 h-5" />
                  <h3 className="font-bold">Editar Venta #{selectedOrder.order_number}</h3>
                </div>
                <button onClick={() => setEditModalOpen(false)} className="p-1 hover:bg-blue-600 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-4 space-y-4">
              {/* Current Order Info */}
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-sm text-muted-foreground">Total actual: <span className="font-bold text-lg">${selectedOrder.total?.toFixed(2)}</span></p>
                <p className="text-xs text-muted-foreground mt-1">
                  {selectedOrder.items?.length || 0} productos
                </p>
              </div>

              {/* Edit Fields */}
              <div className="space-y-3">
                <div>
                  <Label className="text-sm">Nombre del cliente</Label>
                  <Input
                    value={editForm.customer_name}
                    onChange={(e) => setEditForm({...editForm, customer_name: e.target.value})}
                    placeholder="Nombre del cliente"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm">Teléfono</Label>
                  <Input
                    value={editForm.customer_phone}
                    onChange={(e) => setEditForm({...editForm, customer_phone: e.target.value})}
                    placeholder="Teléfono"
                    className="mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">Personas</Label>
                    <Input
                      type="number"
                      min="1"
                      placeholder="Ej: 4"
                      value={editForm.num_guests}
                      onChange={(e) => setEditForm({...editForm, num_guests: e.target.value === '' ? '' : parseInt(e.target.value)})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Descuento ($)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Ej: 50"
                      value={editForm.discount_amount}
                      onChange={(e) => setEditForm({...editForm, discount_amount: e.target.value === '' ? '' : parseFloat(e.target.value)})}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm">Razón del descuento</Label>
                  <Input
                    value={editForm.discount_reason}
                    onChange={(e) => setEditForm({...editForm, discount_reason: e.target.value})}
                    placeholder="Ej: Promoción, cortesía..."
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Required: Reason for edit */}
              <div className="border-t pt-4">
                <Label className="text-sm font-medium text-red-600">Motivo de la edición *</Label>
                <textarea
                  value={editReason}
                  onChange={(e) => setEditReason(e.target.value)}
                  placeholder="Ingrese el motivo de esta edición (obligatorio)"
                  className="mt-1 w-full border rounded-lg p-3 text-sm resize-none h-20 focus:ring-2 focus:ring-blue-500"
                  data-testid="edit-reason-input"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setEditModalOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  className="flex-1 bg-blue-500 hover:bg-blue-600"
                  onClick={handleEditOrder}
                  disabled={!editReason.trim()}
                  data-testid="confirm-edit-btn"
                >
                  Guardar Cambios
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* ==================== VOID ORDER MODAL ==================== */}
      {voidModalOpen && selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl w-full max-w-md"
          >
            <div className="p-4 border-b bg-red-500 text-white rounded-t-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Ban className="w-5 h-5" />
                  <h3 className="font-bold">Anular Venta #{selectedOrder.order_number}</h3>
                </div>
                <button onClick={() => setVoidModalOpen(false)} className="p-1 hover:bg-red-600 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-4 space-y-4">
              {/* Warning */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0" />
                <div>
                  <p className="font-medium text-red-800">¿Estás seguro de anular esta venta?</p>
                  <p className="text-sm text-red-700 mt-1">
                    Esta acción marcará la venta como anulada y revertirá el monto de la caja. 
                    La venta quedará registrada para auditoría.
                  </p>
                </div>
              </div>

              {/* Order Summary */}
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Monto a anular:</span>
                  <span className="font-bold text-lg text-red-600">${selectedOrder.total?.toFixed(2)}</span>
                </div>
                <div className="mt-2 text-xs text-muted-foreground">
                  <p>Cliente: {selectedOrder.customer_name || 'General'}</p>
                  <p>{selectedOrder.items?.length || 0} productos</p>
                </div>
              </div>

              {/* Required: Reason for void */}
              <div>
                <Label className="text-sm font-medium text-red-600">Motivo de la anulación *</Label>
                <textarea
                  value={voidReason}
                  onChange={(e) => setVoidReason(e.target.value)}
                  placeholder="Ej: Error en el pedido, cliente canceló, duplicado..."
                  className="mt-1 w-full border border-red-200 rounded-lg p-3 text-sm resize-none h-24 focus:ring-2 focus:ring-red-500"
                  data-testid="void-reason-input"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setVoidModalOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={handleVoidOrder}
                  disabled={!voidReason.trim()}
                  data-testid="confirm-void-btn"
                >
                  <Ban className="w-4 h-4 mr-2" />
                  Anular Venta
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* ==================== AUDIT LOG MODAL ==================== */}
      {auditLogOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col"
          >
            <div className="p-4 border-b bg-slate-800 text-white rounded-t-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <History className="w-5 h-5" />
                  <h3 className="font-bold">Historial de Cambios</h3>
                </div>
                <button onClick={() => setAuditLogOpen(false)} className="p-1 hover:bg-slate-700 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {loadingAudit ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin w-6 h-6 border-2 border-accent border-t-transparent rounded-full" />
                </div>
              ) : auditLogs.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No hay cambios registrados</p>
              ) : (
                <div className="space-y-3">
                  {auditLogs.map((log, idx) => (
                    <div key={idx} className={`p-3 rounded-lg border ${
                      log.action === 'void' ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          log.action === 'void' ? 'bg-red-500 text-white' : 'bg-blue-500 text-white'
                        }`}>
                          {log.action === 'void' ? 'ANULACIÓN' : 'EDICIÓN'}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formatDateTime(log.created_at)}
                        </span>
                      </div>
                      <p className="text-sm"><span className="font-medium">Por:</span> {log.performed_by}</p>
                      <p className="text-sm mt-1"><span className="font-medium">Motivo:</span> {log.reason}</p>
                      {log.changes && Object.keys(log.changes).length > 0 && (
                        <div className="mt-2 text-xs bg-white/50 rounded p-2">
                          <p className="font-medium mb-1">Cambios:</p>
                          {log.changes.previous_status && (
                            <p>Estado anterior: {log.changes.previous_status}</p>
                          )}
                          {log.changes.total_voided && (
                            <p>Monto anulado: ${log.changes.total_voided.toFixed(2)}</p>
                          )}
                          {log.changes.before && log.changes.after && (
                            <>
                              {Object.keys(log.changes.after).map(key => (
                                key !== 'items' && (
                                  <p key={key}>{key}: {String(log.changes.before[key])} → {String(log.changes.after[key])}</p>
                                )
                              ))}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-4 border-t">
              <Button variant="outline" className="w-full" onClick={() => setAuditLogOpen(false)}>
                Cerrar
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* ==================== WHATSAPP NOTIFICATION MODAL ==================== */}
      {showWhatsAppNotification && voidedOrderData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl w-full max-w-md"
          >
            <div className="p-4 border-b bg-green-500 text-white rounded-t-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" />
                  <h3 className="font-bold">Notificar al Gerente</h3>
                </div>
                <button 
                  onClick={() => setShowWhatsAppNotification(false)} 
                  className="p-1 hover:bg-green-600 rounded"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-4 space-y-4">
              {/* Success message */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                <div>
                  <p className="font-medium text-green-800">Venta #{voidedOrderData.order_number} anulada</p>
                  <p className="text-sm text-green-700 mt-1">
                    ¿Deseas enviar una notificación al gerente por WhatsApp?
                  </p>
                </div>
              </div>

              {/* Message Preview */}
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-xs text-muted-foreground mb-2 font-medium">Vista previa del mensaje:</p>
                <div className="bg-white border rounded-lg p-3 text-sm space-y-1">
                  <p className="font-bold text-red-600">🚨 VENTA ANULADA</p>
                  <p><span className="font-medium">Venta:</span> #{voidedOrderData.order_number}</p>
                  <p><span className="font-medium">Monto:</span> ${voidedOrderData.total?.toFixed(2)}</p>
                  <p><span className="font-medium">Cliente:</span> {voidedOrderData.customer_name}</p>
                  <p><span className="font-medium">Motivo:</span> {voidedOrderData.reason}</p>
                  <p><span className="font-medium">Por:</span> {voidedOrderData.voided_by}</p>
                </div>
              </div>

              {/* WhatsApp Number */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MessageCircle className="w-4 h-4 text-green-500" />
                <span>Se enviará a: <span className="font-medium">{whatsappConfig.manager_whatsapp || 'No configurado'}</span></span>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowWhatsAppNotification(false)}
                >
                  Omitir
                </Button>
                <Button
                  className="flex-1 bg-green-500 hover:bg-green-600"
                  onClick={() => {
                    openWhatsAppNotification();
                    setShowWhatsAppNotification(false);
                  }}
                  disabled={!whatsappConfig.manager_whatsapp}
                  data-testid="send-whatsapp-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Enviar WhatsApp
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Pre-Close Summary Modal */}
      {showCloseModal && preCloseSummary && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl"
          >
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-red-600 to-red-700 text-white p-4 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold">CORTE DE CAJA</h2>
                <p className="text-sm opacity-90">Resumen antes de cerrar</p>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                className="text-white hover:bg-white/20"
                onClick={() => setShowCloseModal(false)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="p-6 space-y-6">
              {/* Alerts Section */}
              {(preCloseSummary.alerts?.has_pending || preCloseSummary.alerts?.has_open_tables || preCloseSummary.alerts?.cancelled_count > 0) && (
                <div className="space-y-3">
                  {preCloseSummary.alerts?.has_pending && (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-center gap-2 text-yellow-800 font-medium mb-2">
                        <AlertTriangle className="w-5 h-5" />
                        {preCloseSummary.alerts.pending_orders.length} Órdenes Pendientes
                      </div>
                      <div className="space-y-1 text-sm">
                        {preCloseSummary.alerts.pending_orders.map(o => (
                          <div key={o.order_id} className="flex justify-between text-yellow-700">
                            <span>#{o.order_number} - {o.order_type === 'dine_in' ? `Mesa ${o.table_number}` : o.order_type}</span>
                            <span>${o.total?.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {preCloseSummary.alerts?.has_open_tables && (
                    <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                      <div className="flex items-center gap-2 text-orange-800 font-medium">
                        <Utensils className="w-5 h-5" />
                        {preCloseSummary.alerts.open_tables.length} Mesas Ocupadas: {preCloseSummary.alerts.open_tables.map(t => t.name || t.number).join(', ')}
                      </div>
                    </div>
                  )}
                  {preCloseSummary.alerts?.cancelled_count > 0 && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center gap-2 text-red-800 font-medium mb-2">
                        <Ban className="w-5 h-5" />
                        {preCloseSummary.alerts.cancelled_count} Órdenes Canceladas (${preCloseSummary.alerts.cancelled_total?.toFixed(2)})
                      </div>
                      <div className="space-y-1 text-sm max-h-24 overflow-y-auto">
                        {preCloseSummary.alerts.cancelled_orders.map(o => (
                          <div key={o.order_id} className="flex justify-between text-red-700">
                            <span>#{o.order_number}: {o.cancel_reason}</span>
                            <span>${o.total?.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Cash Reconciliation (Arqueo) */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-gray-600" />
                  ARQUEO DE CAJA
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Fondo Inicial:</span>
                      <span className="font-medium">${preCloseSummary.cash_reconciliation?.initial_amount?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b text-green-700">
                      <span>(+) Ventas en Efectivo:</span>
                      <span className="font-medium">${preCloseSummary.cash_reconciliation?.cash_sales?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b text-green-600">
                      <span>(+) Entradas:</span>
                      <span className="font-medium">${preCloseSummary.cash_reconciliation?.cash_in?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b text-red-600">
                      <span>(-) Salidas:</span>
                      <span className="font-medium">-${preCloseSummary.cash_reconciliation?.cash_out?.toFixed(2)}</span>
                    </div>
                  </div>
                  {/* 3 Cards for Expected Totals */}
                  <div className="grid grid-rows-3 gap-2">
                    <div className="flex flex-col justify-center items-center bg-green-100 rounded-lg p-3 border border-green-300">
                      <span className="text-green-700 text-xs font-medium">TOTAL ESPERADO EFECTIVO</span>
                      <span className="text-2xl font-black text-green-800">${preCloseSummary.cash_reconciliation?.expected_cash?.toFixed(2)}</span>
                    </div>
                    <div className="flex flex-col justify-center items-center bg-blue-100 rounded-lg p-3 border border-blue-300">
                      <span className="text-blue-700 text-xs font-medium">TOTAL VOUCHER (TARJETA)</span>
                      <span className="text-2xl font-black text-blue-800">${(preCloseSummary.sales_by_payment?.tarjeta?.amount || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex flex-col justify-center items-center bg-purple-100 rounded-lg p-3 border border-purple-300">
                      <span className="text-purple-700 text-xs font-medium">TOTAL TRANSFERENCIA</span>
                      <span className="text-2xl font-black text-purple-800">${(preCloseSummary.sales_by_payment?.transferencia?.amount || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sales Breakdown */}
              <div className="grid grid-cols-2 gap-4">
                {/* By Payment Method */}
                <div className="bg-blue-50 rounded-xl p-4">
                  <h3 className="font-bold mb-3 flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-blue-600" />
                    Por Método de Pago
                  </h3>
                  <div className="space-y-2">
                    {Object.entries(preCloseSummary.sales_by_payment || {}).map(([method, data]) => (
                      <div key={method} className="flex justify-between items-center py-1 border-b border-blue-100">
                        <span className="capitalize">{method === 'efectivo' ? '💵 Efectivo' : method === 'tarjeta' ? '💳 Tarjeta' : method === 'transferencia' ? '📱 Transferencia' : method}</span>
                        <div className="text-right">
                          <span className="font-bold">${data.amount?.toFixed(2)}</span>
                          <span className="text-xs text-gray-500 ml-2">({data.count})</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* By Order Type */}
                <div className="bg-green-50 rounded-xl p-4">
                  <h3 className="font-bold mb-3 flex items-center gap-2">
                    <ShoppingCart className="w-4 h-4 text-green-600" />
                    Por Tipo de Orden
                  </h3>
                  <div className="space-y-2">
                    {Object.entries(preCloseSummary.sales_by_type || {}).map(([type, data]) => (
                      <div key={type} className="flex justify-between items-center py-1 border-b border-green-100">
                        <span>{type === 'dine_in' ? '🍽️ Mesas' : type === 'takeout' ? '🛍️ Mostrador' : type === 'delivery' ? '🛵 Domicilio' : data.label || type}</span>
                        <div className="text-right">
                          <span className="font-bold">${data.amount?.toFixed(2)}</span>
                          <span className="text-xs text-gray-500 ml-2">({data.count})</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Totals Summary */}
              <div className="bg-purple-50 rounded-xl p-4">
                <h3 className="font-bold mb-3 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-purple-600" />
                  Resumen de Ventas
                </h3>
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-black text-purple-700">${preCloseSummary.totals?.total_sales?.toFixed(2)}</p>
                    <p className="text-xs text-gray-500">Venta Total</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-black text-purple-700">{preCloseSummary.totals?.total_orders}</p>
                    <p className="text-xs text-gray-500">Órdenes</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-black text-purple-700">${preCloseSummary.totals?.average_ticket?.toFixed(2)}</p>
                    <p className="text-xs text-gray-500">Ticket Promedio</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-black text-green-600">${preCloseSummary.totals?.total_tips?.toFixed(2)}</p>
                    <p className="text-xs text-gray-500">Propinas</p>
                  </div>
                </div>
              </div>

              {/* Cash Movements */}
              {preCloseSummary.movements?.length > 0 && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-bold mb-3 flex items-center gap-2">
                    <ArrowUpDown className="w-4 h-4 text-gray-600" />
                    Movimientos de Efectivo ({preCloseSummary.movements_summary?.count_in + preCloseSummary.movements_summary?.count_out})
                  </h3>
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {preCloseSummary.movements.map((m, idx) => (
                      <div key={idx} className={`flex justify-between items-center py-2 px-3 rounded ${m.type === 'in' ? 'bg-green-100' : 'bg-red-100'}`}>
                        <div className="flex items-center gap-2">
                          {m.type === 'in' ? <Plus className="w-4 h-4 text-green-600" /> : <Minus className="w-4 h-4 text-red-600" />}
                          <span className="text-sm">{m.reason}</span>
                        </div>
                        <span className={`font-bold ${m.type === 'in' ? 'text-green-700' : 'text-red-700'}`}>
                          {m.type === 'in' ? '+' : '-'}${m.amount?.toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Denominations Counting */}
              <div className="bg-amber-50 rounded-xl p-4">
                <h3 className="font-bold mb-3 flex items-center gap-2">
                  <Wallet className="w-4 h-4 text-amber-600" />
                  Conteo de Denominaciones (Opcional)
                </h3>
                <div className="grid grid-cols-2 gap-6">
                  {/* Bills */}
                  <div>
                    <p className="font-medium text-sm text-gray-600 mb-2">Billetes</p>
                    <div className="space-y-2">
                      {denominations.bills.map((d, idx) => (
                        <div key={d.value} className="flex items-center gap-2">
                          <span className="w-24 text-sm">{d.label}</span>
                          <Input
                            type="number"
                            min="0"
                            className="w-20 h-8 text-center"
                            value={d.count || ''}
                            onChange={(e) => updateDenomination('bills', idx, e.target.value)}
                            placeholder="0"
                          />
                          <span className="text-sm text-gray-500 w-24 text-right">
                            ${(d.value * d.count).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Coins */}
                  <div>
                    <p className="font-medium text-sm text-gray-600 mb-2">Monedas</p>
                    <div className="space-y-2">
                      {denominations.coins.map((d, idx) => (
                        <div key={d.value} className="flex items-center gap-2">
                          <span className="w-24 text-sm">{d.label}</span>
                          <Input
                            type="number"
                            min="0"
                            className="w-20 h-8 text-center"
                            value={d.count || ''}
                            onChange={(e) => updateDenomination('coins', idx, e.target.value)}
                            placeholder="0"
                          />
                          <span className="text-sm text-gray-500 w-24 text-right">
                            ${(d.value * d.count).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                {calculateDenominationsTotal() > 0 && (
                  <div className="mt-4 p-3 bg-white rounded-lg border-2 border-amber-300">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Total Contado:</span>
                      <span className="text-2xl font-black">${calculateDenominationsTotal().toFixed(2)}</span>
                    </div>
                    <div className={`text-sm mt-1 ${calculateDenominationsTotal() === preCloseSummary.cash_reconciliation?.expected_cash ? 'text-green-600' : 'text-red-600'}`}>
                      {calculateDenominationsTotal() === preCloseSummary.cash_reconciliation?.expected_cash 
                        ? '✓ El monto coincide con lo esperado'
                        : `Diferencia: $${(calculateDenominationsTotal() - preCloseSummary.cash_reconciliation?.expected_cash).toFixed(2)}`
                      }
                    </div>
                  </div>
                )}
              </div>

              {/* Manual Amount (if not using denominations) */}
              {calculateDenominationsTotal() === 0 && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-bold mb-3">O ingresa el monto total manualmente:</h3>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={closeAmount}
                      onChange={(e) => setCloseAmount(e.target.value)}
                      className="pl-10 text-xl h-12"
                      data-testid="close-amount-input"
                    />
                  </div>
                </div>
              )}

              {/* Notes */}
              <div>
                <Label className="font-medium">Notas del Cierre (Opcional)</Label>
                <textarea
                  className="w-full mt-2 p-3 border rounded-lg resize-none"
                  rows={2}
                  placeholder="Observaciones, novedades del turno..."
                  value={closeNotes}
                  onChange={(e) => setCloseNotes(e.target.value)}
                />
              </div>

              {/* Actions */}
              <div className="flex gap-4 pt-4 border-t">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowCloseModal(false)}
                >
                  Cancelar
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={closeCashRegister}
                  disabled={calculateDenominationsTotal() === 0 && (!closeAmount || parseFloat(closeAmount) < 0)}
                  data-testid="confirm-close-btn"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Confirmar Cierre
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Delete Movement Modal (Admin Only) */}
      {deleteMovementModal && movementToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="bg-red-600 text-white p-4 rounded-t-xl">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Trash2 className="w-5 h-5" />
                Eliminar Movimiento
              </h3>
            </div>
            <div className="p-4 space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-500">Movimiento a eliminar:</p>
                <p className="font-bold text-lg">
                  {movementToDelete.movement_type === 'in' || movementToDelete.movement_type === 'sale' ? '+' : '-'}
                  ${movementToDelete.amount?.toFixed(2)}
                </p>
                <p className="text-sm">{movementToDelete.reason || 'Sin motivo'}</p>
                <p className="text-xs text-gray-400">
                  {movementToDelete.created_at ? new Date(movementToDelete.created_at).toLocaleString('es-MX') : ''}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Motivo de eliminación <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder="Escribe el motivo por el cual eliminas este movimiento..."
                  className="w-full border rounded-lg p-2 text-sm min-h-[80px]"
                  required
                />
              </div>
              
              <div className="bg-yellow-50 p-3 rounded-lg flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  <p className="font-medium">Esta acción no se puede deshacer</p>
                  <p>Los totales de caja se actualizarán automáticamente.</p>
                </div>
              </div>
            </div>
            <div className="p-4 border-t flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setDeleteMovementModal(false);
                  setMovementToDelete(null);
                  setDeleteReason('');
                }}
              >
                Cancelar
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                onClick={handleDeleteMovement}
                disabled={deletingMovement || !deleteReason.trim()}
              >
                {deletingMovement ? 'Eliminando...' : 'Eliminar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CashRegister;
